import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { setApiStoreId, setApiActorId } from "./lib/apiScope";
import { reportSelfHeal } from "./lib/watchdog";
import { trackView } from "./lib/usage";
import UpdateManager from "./components/UpdateManager";
import SuspensionAppealModal from "./components/SuspensionAppealModal";
import TutorialOverlay, { WelcomePrompt, hasSeenTutorial, clearTutorialSeen } from "./components/TutorialOverlay";
import TransferRequestSheet from "./components/TransferRequestSheet";
import PinResetRequestsBanner from "./components/PinResetRequestsBanner";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import HomeScreen from "./pages/HomeScreen";
import CrewTallyPage from "./pages/CrewTallyPage";
import ProfilePage from "./pages/ProfilePage";
import TeamPage from "./pages/TeamPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ContestPage from "./pages/ContestPage";
import RecognitionPage from "./pages/RecognitionPage";
// Heart Balloons + Dumpling pages deleted 2026-05-19 — campaigns ended, user
// requested full code removal vs feature-flag dormancy. Re-instate via
// `git revert` if a future Mother's Day cycle wants them back.
import ZoneSheetPrintPage from "./pages/ZoneSheetPrintPage";
import { Dialog, DialogContent, DialogTitle } from "./components/ui/dialog";
import MaintenancePage from "./pages/MaintenancePage";
import useScreenshotDetect from "./hooks/useScreenshotDetect";
import { useRealtimeNotifications } from "./hooks/useRealtimeNotifications";
import WelcomeSplash from "./components/WelcomeSplash";
import StoreSwapToggle, { saveLastCoveredStore } from "./components/StoreSwapToggle";
import SpiderManTheme from "./components/SpiderManTheme";
import HalloweenTheme from "./components/HalloweenTheme";
import PitchBlackTheme from "./components/PitchBlackTheme";
import PitchBlackDock from "./components/PitchBlackDock";
import { UrgentAlertBanner } from "./components/UrgentCoverage";
import DailyBriefing from "./components/DailyBriefing";
import RecognitionCelebration from "./components/RecognitionCelebration";
import BirthdayWall from "./components/BirthdayWall";
import { House as PBHouse, ClipboardText as PBClipboard, ChatCircle as PBChat, ChartBar as PBChart } from "@phosphor-icons/react";
import { useTheme } from "./ThemeContext";
import pkg from "../package.json";

// Route-level code split (2026-02-28) — only the 6 heaviest pages are
// lazy. Everything else stays eager so the "nav feels lazy" complaint
// from 2026-05-02 doesn't come back. We ALSO warm the lazy chunks in
// the background after initial mount (see the preload effect below)
// so by the time a manager taps Inbox / Manager / Zone Sheet, the JS
// is already cached — tap is instant, first paint is 60% smaller.
const ManagerDashboard = lazy(() => import(/* webpackPrefetch: true */ "./pages/ManagerDashboard"));
const InboxPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/InboxPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const ZoneSheetPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ZoneSheetPage"));
const SystemHealthPage = lazy(() => import("./pages/SystemHealthPage"));
// Calendar (date libs) and the DM-only District dashboard (charts) are only
// reached after login and not on the hot path, so they're code-split out of
// the main bundle to shrink cold-start first paint (user report 2026-06:
// "entire app slow during cold start").
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const DistrictDashboard = lazy(() => import("./pages/DistrictDashboard"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compare two "M.m.p"-ish version strings. Returns true iff `a` is strictly
// newer than `b`. Non-numeric segments are treated as 0 so a malformed tag
// never accidentally triggers a false-positive reload. Used by the new-build
// poller below so a client bundle that happens to be AHEAD of the server
// (e.g. while a rolling deploy is in progress) doesn't spam the crew with
// "Update available" toasts. (user report 2026-05-04 infinite 4.0.2→4.0.0 loop.)
const isNewer = (a, b) => {
  const parse = (v) => String(v || "").split(".").map((n) => parseInt(n, 10) || 0);
  const [aa, ab, ac] = parse(a);
  const [ba, bb, bc] = parse(b);
  if (aa !== ba) return aa > ba;
  if (ab !== bb) return ab > bb;
  return ac > bc;
};

/**
 * Build the "effective user" view of the signed-in session, accounting for
 * Store Mirror mode. (Added 2026-05-19 — user ask: "When I mirror a store
 * I only want to see what they see, not what I can see as an admin".)
 *
 * Strategy: keep `user` as the source of truth for AUTHORIZATION (who can
 * write what, on the backend), but synthesize a separate `effectiveUser`
 * for the UI tree when mirroring. The mirror persona:
 *   - Adopts the target store's `home_store_id` / code / city so store-
 *     scoped components (StoriesRow gate, splash, welcome banner) see
 *     the mirrored store.
 *   - Drops `is_admin` + `is_district_manager` so the Admin / District
 *     tiles disappear from the home grid.
 *   - Keeps `is_manager` if the admin was a manager (since SMs of the
 *     mirrored store would also be managers — closer to "what they see").
 *
 * The unsynthesized `user` is still passed to LoginPage / Settings /
 * Admin pages so the admin can ALWAYS exit mirror mode and write to the
 * backend with their real privileges. We never strip those rights from
 * the auth identity — only from the UI tree below this helper.
 */
function effectiveUser(user, viewingStoreId, viewingStore, viewingAsDM) {
  if (viewingAsDM && user?.is_admin) {
    // "View as District Manager" mode — strip admin omniscience and
    // pretend the actor IS the DM so HomePage's tile filter (DM gets
    // District + What's Up + Settings only) renders correctly. We KEEP
    // is_district_manager true so the DM tile + chrome flow works.
    // (User report 2026-05-21 "when I mirror the DM I can only see her
    // dashboard, can't see her home page layout".)
    return {
      ...user,
      is_admin: false,
      is_manager: false,
      is_district_manager: true,
      // Preserve the REAL admin identity so admin-only sentinels that
      // SHOULD survive mirror mode (e.g. the "+ ADMIN" promotion button
      // in the sister-store roster — only an actual admin is allowed to
      // grant admin) can still recognize this viewer. UI surfaces that
      // grant store-manager-level powers continue to use `is_admin`
      // which is false — they get the mirrored view.
      _real_is_admin: true,
      _mirroring: true,
      _mirroringDM: true,
    };
  }
  if (!user || !viewingStoreId || !viewingStore) return user;
  return {
    ...user,
    is_admin: false,
    is_district_manager: false,
    home_store_id: viewingStoreId,
    home_store_code: viewingStore.code || "",
    home_store_name: viewingStore.name || "",
    home_store_city: viewingStore.city || "",
    // Same rationale as above — admin promotions are gated separately
    // from the broader mirror-strip so the actor doesn't lose the
    // ability to promote a sister-store SM to admin during a mirror
    // session. (User report 2026-05-23: "admin button isn't loading on
    // other store roaster when promoted to manager … only admin can
    // make admin".)
    _real_is_admin: !!user.is_admin,
    _mirroring: true,
  };
}

// Goodbye splash — compact centered card mirroring the WelcomeSplash V2
// aesthetic. Redesigned 2026-02-26 (user: "since the V2 launch the splash
// screen is too much its taking too much of the screen") to feel like a
// pop-out modal rather than a full-screen takeover. Header strip is a
// slate/blue "sunset" gradient with a waving-hand emoji; card body
// carries the "Bye, {name}!" headline + time-of-day sign-off.
//
// Phase lifecycle (unchanged so App.js timing still applies):
//   1. mount → phase="in"  → card scales in, backdrop fades up
//   2. +1700ms → phase="off" → card CRT-off collapses (still centered)
//   3. +2400ms → parent unmounts → real logout runs
function GoodbyeSplash({ name, tod, phase }) {
  const isOff = phase === "off";
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      style={{
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        animation: "fadeIn 220ms ease-out forwards",
      }}
      data-testid="goodbye-splash"
    >
      <div
        className={`relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl bg-white ${isOff ? "crt-off-collapse" : ""}`}
        style={{
          transition: "transform 700ms cubic-bezier(0.7, 0, 0.84, 0)",
          transformOrigin: "center",
          animation: !isOff ? "welcomeCardIn 280ms cubic-bezier(0.16, 1, 0.3, 1) forwards" : undefined,
        }}
      >
        {/* Header strip — Five-Below blue → deep navy gradient with a
            waving-hand emoji floating above. Compact accent, not a
            full-screen takeover. */}
        <div
          className="relative px-6 pt-6 pb-4 text-center"
          style={{ background: "linear-gradient(135deg, #0066FF 0%, #1E40AF 55%, #0F172A 100%)" }}
        >
          <div
            className="text-5xl mb-1.5 select-none inline-block"
            style={{
              filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.25))",
              animation: "welcomeArtFloat 2.6s ease-in-out infinite",
            }}
            aria-hidden
          >
            👋
          </div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-black text-white/90">
            See you soon
          </p>
        </div>

        {/* Sign-off copy — matches WelcomeSplash card body styling so
            login + logout feel like a single visual language. */}
        <div className="px-6 py-5 text-center">
          <h1
            className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight"
            style={{
              fontFamily: "'Five Below Gothic', Chivo, sans-serif",
              letterSpacing: "-0.01em",
            }}
            data-testid="goodbye-greeting"
          >
            Bye, {name}!
          </h1>
          <p className="mt-2 text-sm font-medium text-slate-600">
            Have a great {tod}. Now go and have fun. ✨
          </p>
        </div>
      </div>
      <style>{`
        @keyframes welcomeCardIn {
          from { opacity: 0; transform: scale(0.9) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes welcomeArtFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}

function App() {
  // Spider-Man theme detection — matches the SettingsPage preset's
  // unique color signature (rose-600 primary + blue-800 accent on the
  // dark bg). We can't just check for a "theme name" because presets
  // save colors, not names. This palette combo isn't used by any
  // other preset so it's an unambiguous match.
  const { settings: activeThemeSettings } = useTheme();
  const isSpiderTheme = (() => {
    if (!activeThemeSettings) return false;
    const p = (activeThemeSettings.primary_color || "").toLowerCase();
    const a = (activeThemeSettings.accent_color || "").toLowerCase();
    return p === "#dc2626" && a === "#1e40af";
  })();

  // Halloween theme detection — matches the SettingsPage Halloween preset
  // (primary #EA580C + accent #7C2D12). Trigger only for that combo so the
  // atmosphere overlay never activates from a look-alike custom palette.
  const isHalloweenTheme = (() => {
    if (!activeThemeSettings) return false;
    const p = (activeThemeSettings.primary_color || "").toLowerCase();
    const a = (activeThemeSettings.accent_color || "").toLowerCase();
    return p === "#ea580c" && a === "#7c2d12";
  })();

  // Pitch Black theme detection — matches the SettingsPage Pitch Black
  // preset (neon magenta #EC008C + lime #A3E635). Unique combo so the
  // neon overlay never activates from a look-alike custom palette.
  const isPitchBlackTheme = (() => {
    if (!activeThemeSettings) return false;
    const p = (activeThemeSettings.primary_color || "").toLowerCase();
    const a = (activeThemeSettings.accent_color || "").toLowerCase();
    return p === "#ec008c" && a === "#a3e635";
  })();

  // Konami-style "wow" easter egg — type "wow" three times anywhere
  // in the app and hero mode unlocks for 6s. Listener is always
  // mounted (cheap) but only actually fires if the theme is active.
  useEffect(() => {
    if (!isSpiderTheme) return;
    let buf = "";
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if (k.length !== 1) return;
      buf = (buf + k).slice(-9); // keep last 9 chars — "wowwowwow"
      if (buf === "wowwowwow") {
        window.dispatchEvent(new Event("spider:hero"));
        buf = "";
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSpiderTheme]);

  // (Lazy-chunk warming moved below — it now waits until AFTER login and
  // only warms the pages a given role actually uses, so a cold start on
  // store wifi isn't clogged downloading manager-only chunks. See the
  // role-aware warm effect further down.)

  const [user, setUser] = useState(null);

  // Role-aware lazy-chunk warming — runs ONLY after login, so a cold start
  // never downloads manager chunks while someone is still on the sign-in
  // screen (and crew never fetch manager-only pages at all). Chunks warm one
  // at a time, on idle with a gap, so a slow store connection isn't saturated
  // right when the user opens Messages (user report 2026-06: cold start slow,
  // "especially messages when you first open"). Inbox warms first for everyone.
  useEffect(() => {
    if (!user) return undefined;
    const isMgr = !!(user.is_manager || user.is_admin || user.is_district_manager);
    const isAdmin = !!user.is_admin;
    const queue = [() => import("./pages/InboxPage")];
    if (isMgr) {
      queue.push(() => import("./pages/ManagerDashboard"));
      queue.push(() => import("./pages/ZoneSheetPage"));
      queue.push(() => import("./pages/SettingsPage"));
    }
    if (isAdmin) {
      queue.push(() => import("./pages/ReportsPage"));
      queue.push(() => import("./pages/AdminPage"));
    }
    let i = 0, cancelled = false;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
    const pump = () => {
      if (cancelled || i >= queue.length) return;
      try { queue[i](); } catch { /* ignore */ }
      i += 1;
      setTimeout(() => idle(pump), 1200);
    };
    const start = setTimeout(() => idle(pump), 2500);
    return () => { cancelled = true; clearTimeout(start); };
  }, [user]);

  // Seed crew from sessionStorage so the HomePage avatars, the Team list, the
  // InboxPage convo lookups, etc. all paint INSTANTLY on reload instead of
  // waiting for the network. Revalidated right after mount.
  //
  // 2026-02-28 stale-cache guard: after we moved crew avatars from
  // inline base64 to lazy `/api/crew/{id}/avatar` URLs, any user with
  // an old sessionStorage payload was still parsing (and re-shipping
  // to <img src>) a 1.7 MB blob on every reload. Detect the legacy
  // format (photo_url starts with `data:`) and drop the whole cache
  // — fetchCrew below will repopulate with tiny URL strings.
  const [crew, setCrew] = useState(() => {
    try {
      const cached = sessionStorage.getItem("ecDashCrew");
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      const hasLegacyBase64 = Array.isArray(parsed) && parsed.some((c) => typeof c?.photo_url === "string" && c.photo_url.startsWith("data:"));
      if (hasLegacyBase64) {
        sessionStorage.removeItem("ecDashCrew");
        return [];
      }
      return parsed;
    } catch { /* ignore */ }
    return [];
  });
  // Service-worker-driven cache purge (2026-02-28). SW v3.1.0 broadcasts
  // a `wow:cache-purge` message on activate so newly-updated PWAs drop
  // any stale sessionStorage/localStorage from before the avatar URL
  // migration. Runs at mount so the message isn't missed if the SW
  // activated during the previous tab close.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const onMessage = (e) => {
      if (e?.data?.type !== "wow:cache-purge") return;
      try {
        sessionStorage.removeItem("ecDashCrew");
        // Drop any other perf-sensitive caches that a stale schema
        // could bloat. localStorage themes/pins are tiny; leave them.
      } catch { /* ignore */ }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);
  const [loading, setLoading] = useState(() => {
    try { return !sessionStorage.getItem("ecDashCrew"); } catch { return true; }
  });
  // showHome defaults to TRUE so the moment `user` becomes truthy, the
  // Home tile picker renders — never the role-based fallback page. This
  // fixes the "Collect Emails tab flashes on first login" reported by
  // user 2026-06-12: the old default (false) meant any timing gap between
  // setUser and setShowHome(true) let the conditional fallback at the
  // bottom of the JSX (CrewTallyPage / ManagerDashboard) render for one
  // commit before Home took over. With Home defaulting to true, the
  // fallback can never render unless an explicit nav action set it.
  const [showHome, setShowHome] = useState(true);
  // Birthday wall — active card for the user's store today (or null).
  const [bdayCard, setBdayCard] = useState(null);
  const [wallCard, setWallCard] = useState(null);
  const [showBdayWall, setShowBdayWall] = useState(false);
  const [belatedCards, setBelatedCards] = useState([]);
  // Boot failsafe (2026-06-21): a hung boot request (crew/avatars/auth
  // refresh on a flaky Android network) could leave `loading` true
  // forever → permanent spinner / blank. Guarantee the app renders
  // within 10s no matter what; the individual fetches still resolve
  // and fill state when they land.
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(t);
  }, []);

  // Fetch today's active birthday card for the user's store (drives the
  // wall + the Daily Briefing row + the notification-tap deep-link).
  useEffect(() => {
    const cid = user?.crew_id;
    if (!cid) { setBdayCard(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/birthday/active`, { params: { actor_id: cid } });
        if (!cancelled) setBdayCard((r.data?.cards || [])[0] || null);
      } catch { if (!cancelled) setBdayCard(null); }
    })();
    return () => { cancelled = true; };
  }, [user?.crew_id]);

  // Recent celebrations the user missed signing — powers the Home "You missed
  // a celebration" belated strip. `belatedNonce` lets us refetch after the
  // user signs a belated card (so it drops off their strip).
  const [belatedNonce, setBelatedNonce] = useState(0);
  useEffect(() => {
    const cid = user?.crew_id;
    if (!cid) { setBelatedCards([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/birthday/belated`, { params: { actor_id: cid } });
        if (!cancelled) setBelatedCards(r.data?.cards || []);
      } catch { if (!cancelled) setBelatedCards([]); }
    })();
    return () => { cancelled = true; };
  }, [user?.crew_id, belatedNonce]);
  const [showInbox, setShowInbox] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReports, setShowReports] = useState(false);
  // Lets callers deep-link to a specific tab inside ReportsPage. Right now the
  // What's New popover passes "changelog" so "Full log" lands you on the
  // version history tab directly (user ask 2026-02-14). Resets after one use
  // so the next bare ReportsPage open defaults to "recap" again.
  const [initialReportsTab, setInitialReportsTab] = useState("");
  // Lets the Reports → Zone Sheets tab open the ZoneSheetPage at a
  // specific historical date. Reset on every open via setZoneSheetInitialDate("").
  const [zoneSheetInitialDate, setZoneSheetInitialDate] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showContest, setShowContest] = useState(false);
  const [showRecognition, setShowRecognition] = useState(false);
  const [recognitionPreset, setRecognitionPreset] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [showZoneSheet, setShowZoneSheet] = useState(false);
  // Home-screen slide-up sheets. When set, we render the target page
  // inside a bottom Sheet over Home instead of navigating away. Empties
  // out when the user closes the sheet.
  //   'team' | 'leaderboard' | 'calendar' | 'contest' | null
  const [homeSheetPage, setHomeSheetPage] = useState(null);
  const closeHomeSheet = () => setHomeSheetPage(null);
  // Legacy V1 home escape hatch — set via `?legacy=1` URL param so we can
  // fall back to the old HomePage if V2 has a regression. Removes after
  // one week of clean V2 uptime. (User 2026-08-06: "lets lock in V2".)
  const useLegacyHome = (() => {
    try { return new URLSearchParams(window.location.search).get("legacy") === "1"; }
    catch { return false; }
  })();
  // 2026-05-19: dedicated Admin page (cross-store/system controls) extracted
  // from Settings to keep that page focused on per-store config.
  const [showAdmin, setShowAdmin] = useState(false);
  // 2026-05-19: District Manager dashboard — cross-store overview of every
  // store in the user's district. Admin can also reach this via the
  // "View as DM" button on the Admin console.
  const [showDistrict, setShowDistrict] = useState(false);

  // 2026-02-28: System Health / Watchdog admin console.
  const [showSystemHealth, setShowSystemHealth] = useState(false);

  // Forced-logout breadcrumb — every site that wipes the session
  // stamps a reason here. The LoginPage reads it on mount and shows
  // a banner ("Your session expired" / "Store paused for
  // maintenance" / etc.) so users aren't silently dumped back to
  // login without explanation. (User ask 2026-06-12 "do not silently
  // dump the user back to login without explanation".) The console
  // tag makes the reason searchable in DevTools and remote-debug
  // logs when investigating "why was I signed out".
  const recordLogoutReason = (reason) => {
    try { sessionStorage.setItem("ecDashLogoutReason", String(reason || "unknown")); } catch {}
    try { console.warn("[wow:auth] forced logout →", reason); } catch {}
  };
  // Soft re-lock (personal device, 7-day window elapsed). Unlike a full
  // logout this KEEPS the refresh token + soft-lock hint so the reload
  // lands the crew on the quick PIN-unlock screen (avatar + PIN pad)
  // instead of the full Employee-ID sign-in. Only shared devices ever do
  // a hard logout on expiry.
  const softLockPunt = (reason) => {
    recordLogoutReason(reason);
    try {
      localStorage.removeItem("ecDashUser");
      localStorage.removeItem("ecDashSession");
      sessionStorage.removeItem("ecDashUser");
    } catch { /* ignore */ }
    try { window.location.replace(window.location.pathname); }
    catch { window.location.href = "/"; }
  };
  // 2026-05-19: "View as Store" mirror mode. When admin/DM opens a sister
  // store from the Admin console, we set `viewingStoreId` to that store's
  // id. The whole app then routes data fetches scoped to that store so
  // admin sees the full experience exactly as crew at the target store
  // would. A persistent banner offers "Return to 3094".
  // null/empty = no mirror (default behavior, scoped to user's home store).
  const [viewingStoreId, setViewingStoreId] = useState("");
  const [viewingStore, setViewingStore] = useState(null);  // full doc for banner display
  // 2026-05-21: "View as District Manager" mirror mode. Toggled from the
  // Admin console — strips admin identity in the UI tree so HomePage's
  // DM-tile filter renders correctly when the admin taps Home from the
  // DM Dashboard. The persistent banner ("Viewing as District Manager"
  // + Return) is the source of truth for the actor's awareness.
  const [viewingAsDM, setViewingAsDM] = useState(false);

  // Auto-visiting scope (2026-02-26) — when a manager is on another
  // store's zone sheet today AND the current time falls within their
  // shift, we auto-scope the whole app to that visiting store (same
  // apiStore plumbing admin mirror mode uses, but self-triggered).
  // Clears the moment the shift ends → app returns to home store with
  // zero user action. User spec: "if a manager is visiting on they're
  // on the zone sheet it should put them at that visiting store for
  // that shift duration no pills or nothing just need it to work
  // across the board then when there shift is over they return back
  // to their store".
  const [autoVisitingStoreId, setAutoVisitingStoreId] = useState("");
  useEffect(() => {
    if (!user?.crew_id) { setAutoVisitingStoreId(""); return undefined; }
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/my-visiting-scope`, {
          params: { actor_id: user.crew_id },
        });
        if (cancelled) return;
        const sid = (r.data && r.data.store_id) || "";
        setAutoVisitingStoreId((cur) => (cur === sid ? cur : sid));
      } catch { /* silent — falls back to home store */ }
    };
    check();
    // Re-poll every 60s so shift-end triggers a snap back to home store
    // without the manager having to log out / reload.
    const int = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(int); };
  }, [user?.crew_id]);

  // Memoized mirror-aware user — strips admin/DM flags and swaps the
  // home store identity when admin is mirroring. Everything below the
  // App tree should consume `viewUser` for UI decisions (which tiles to
  // show, what store header to render, etc.). The raw `user` is reserved
  // for backend writes (actor_id) where admin's real privileges apply.
  const viewUser = useMemo(
    () => effectiveUser(user, viewingStoreId, viewingStore, viewingAsDM),
    [user, viewingStoreId, viewingStore, viewingAsDM],
  );

  // Multi-tenant API scoping (2026-05-19 — fixes Store Mirror data leak).
  // Sync the module-level apiScope helper with the active store context:
  //   - Mirror mode → use the sister store's id (admin viewing Lakewood).
  //   - No mirror   → use the signed-in user's home_store_id.
  //   - Logged out  → clear it (let backend default to STORE_3094_ID).
  // This single source of truth means every axios call across the app
  // automatically carries the correct `?store_id=` param, so the
  // backend's `_sid()` helper filters queries to the right tenant.
  //
  // Whenever the scope changes (login, logout, mirror in/out), we also
  // refetch the crew list so the Wow Crew row / Team page / Inbox
  // recipient pickers all reflect the new store's roster.
  useEffect(() => {
    let nextId = "";
    if (viewingStoreId) nextId = viewingStoreId;
    else if (autoVisitingStoreId) nextId = autoVisitingStoreId;
    else if (user?.home_store_id) nextId = user.home_store_id;
    setApiStoreId(nextId);
    // 2026-08-29 — also publish the signed-in actor so the axios
    // interceptor auto-attaches `actor_id` on every API call. This
    // powers the auth-gate on business endpoints (tally, biz, wtd-,
    // mtd-, weekly-, daily-*, crew-performance, messages, events).
    setApiActorId(user?.crew_id || "");
    // Re-fetch crew the moment the scope changes so we show the right
    // store's people. Skip during initial mount (the first crew fetch is
    // already running in the mount effect above and we don't want a
    // double-fetch race on cold load).
    if (user) {
      (async () => {
        try {
          const [listRes, avatarsRes] = await Promise.all([
            axios.get(`${API}/crew`, { params: { active_only: true } }),
            axios.get(`${API}/crew/avatars`),
          ]);
          const avatars = avatarsRes.data || {};
          const merged = (listRes.data || []).map((c) => ({
            ...c,
            photo_url: avatars[c.id] || "",
          }));
          setCrew(merged);
          try { sessionStorage.setItem("ecDashCrew", JSON.stringify(merged)); } catch { /* quota */ }
        } catch (e) {
          console.error("[scope] crew refetch failed", e);
          // 2026-09-02 — user report: "we can't even assign crew, the
          // dropdown is missing". Root cause was a silent crew fetch
          // failure on scope change (session cookie expiry or transient
          // network blip) leaving `crew` empty, which cascaded into
          // ZoneSheetPage's CrewEditor hiding the picker entirely.
          // Fallback: if the fetch fails BUT sessionStorage still has a
          // roster, don't blow that away — keep the previous list so
          // pages that depend on `crew` keep working. If sessionStorage
          // is ALSO empty and we currently have zero crew in state,
          // wait 3s and try once more (covers Atlas failover blips).
          try {
            const cached = sessionStorage.getItem("ecDashCrew");
            if (cached) {
              const parsed = JSON.parse(cached);
              if (Array.isArray(parsed) && parsed.length > 0) {
                setCrew(parsed);
                return;
              }
            }
          } catch { /* ignore */ }
          // Retry once after 3s if we're completely empty.
          setTimeout(async () => {
            try {
              const [rl, ra] = await Promise.all([
                axios.get(`${API}/crew`, { params: { active_only: true } }),
                axios.get(`${API}/crew/avatars`),
              ]);
              const av = ra.data || {};
              const m = (rl.data || []).map((c) => ({ ...c, photo_url: av[c.id] || "" }));
              if (m.length > 0) {
                setCrew(m);
                try { sessionStorage.setItem("ecDashCrew", JSON.stringify(m)); } catch { /* quota */ }
              }
            } catch { /* still silent — the CrewEditor now has a Reload Roster fallback */ }
          }, 3000);
        }
      })();
    }
    // Broadcast so individual pages (HomePage, ManagerDashboard, etc.) can
    // refetch their per-store data without each page needing to mirror
    // the App.js mirror-state.
    try {
      window.dispatchEvent(new CustomEvent("wow:scope-changed", { detail: { storeId: nextId } }));
    } catch { /* ignore */ }
  }, [viewingStoreId, autoVisitingStoreId, user]);
  // Heart Balloons + Dumpling state hooks deleted 2026-05-19 along with the
  // pages themselves. Routes that used to wake them (e.g. `/balloons`,
  // `/dumpling`) now fall through to the home grid.
  // Maintenance gate — flips true when admin toggles maintenance_mode in
  // Settings. Polled from /api/settings every 30s so non-admin sessions get
  // hard-logged-out within a poll cycle (user spec 2026-05-03 "kicks them
  // to a maintenance screen the next time their app polls"). Admins
  // (is_admin=true) bypass entirely. State lives at App level so the gate
  // wraps the entire post-login render tree.
  const [maintenance, setMaintenance] = useState(false);
  // Tracks "is EITHER lockout flag on" regardless of whether the current
  // user is bypassed — drives the LoginPage's "getting some updates"
  // banner so pre-login users get a heads-up. Distinct from `maintenance`
  // (which flips the full-screen MaintenancePage for users who ARE being
  // blocked). Set in the lockout poll below.
  const [lockoutBanner, setLockoutBanner] = useState(false);
  // Admin-editable banner copy — surfaces in the LoginPage banner + the
  // post-login MaintenancePage screen. Mirrors `settings.pause_banner_message`
  // and falls back to a sensible default if blank. (User ask 2026-06-08:
  // "we need to be able to edit the text if we need to".)
  const [pauseBannerMessage, setPauseBannerMessage] = useState("");
  // When the hidden 5-tap escape on MaintenancePage fires, we flip this so
  // the LoginPage renders (admins can sign in). Reset whenever we leave
  // logged-out state (login success) or when maintenance toggles off so a
  // future maintenance window doesn't auto-skip the gate. (User ask
  // 2026-02: "the login shouldn't even show only the message should appear
  // admin will see the login page with the message".)
  const [maintenanceBypass, setMaintenanceBypass] = useState(false);
  // Goodbye splash state (2026-05-25). When set, renders a full-screen
  // "Bye, Dom!" card with a time-of-day greeting, then runs the CRT-off
  // collapse animation before `handleLogout` actually wipes the session.
  const [goodbye, setGoodbye] = useState(null);
  // Welcome splash (2026-02) — mirrors the goodbye. Full-screen "Hi,
  // {name}!" + time-of-day art + yesterday's stat brag + confetti. Set
  // by `handleLogin` when this crew_id hasn't seen the splash yet
  // today (MST). One entry per day per crew — never noisy on
  // back-to-back sessions.
  const [welcome, setWelcome] = useState(null);
  // Tutorial state. `showWelcomePrompt` = first-login splash with Tour/Skip.
  // `showTutorial` = the actual carousel. Settings can replay via the
  // "wow:tutorial-replay" DOM event so we don't have to prop-drill across
  // every page.
  const [showWelcomePrompt, setShowWelcomePrompt] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const replay = () => {
      clearTutorialSeen(user?.crew_id);
      setShowWelcomePrompt(false);
      setShowTutorial(true);
    };
    window.addEventListener("wow:tutorial-replay", replay);
    return () => window.removeEventListener("wow:tutorial-replay", replay);
  }, [user?.crew_id]);
  // When the Team tree user taps "Message", we stash the target crew id so the
  // Inbox page can auto-open (or start) that DM thread on mount.
  const [inboxDmCrewId, setInboxDmCrewId] = useState("");

  const fetchCrew = useCallback(async () => {
    // Only hit the authed roster endpoint once someone is actually signed
    // in — /api/crew requires the lp_session cookie, so firing it on the
    // login screen just spammed the console with 401s (and set loading).
    if (!user) { setLoading(false); return; }
    try {
      // Parallel fetch: slim crew list + avatars map. The avatars map
      // was rewritten 2026-02-28 to return LAZY URLs (`/api/crew/{id}/avatar`)
      // instead of inline base64 dataURIs — dropped the cold-open
      // payload from 1.7 MB → ~2 KB. Individual photos stream on
      // demand and get browser-cached for a week, so scrolls stay
      // instant after first paint.
      const [listRes, avatarsRes] = await Promise.all([
        axios.get(`${API}/crew`, { timeout: 20000, params: { active_only: true } }),
        axios.get(`${API}/crew/avatars`, { timeout: 20000 }),
      ]);
      const avatars = avatarsRes.data || {};
      const merged = (listRes.data || []).map((c) => ({
        ...c,
        photo_url: avatars[c.id] || "",
      }));
      setCrew(merged);
      try { sessionStorage.setItem("ecDashCrew", JSON.stringify(merged)); } catch { /* quota */ }
    } catch (e) {
      console.error("Failed to fetch crew:", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchCrew(); }, [fetchCrew]);

  // Live roster refresh — when a crew member is added in Settings (or an
  // admin console) we fire `wow:refresh-crew` so the in-memory roster reloads
  // WITHOUT a logout/login. Fixes: a newly enrolled crew member didn't appear
  // in the Zone Sheet add-crew dropdown until the admin signed out and back in.
  useEffect(() => {
    const onRefresh = () => { fetchCrew(); };
    window.addEventListener("wow:refresh-crew", onRefresh);
    return () => window.removeEventListener("wow:refresh-crew", onRefresh);
  }, [fetchCrew]);

  // Open the celebration wall from an in-app notification tap. The bell/notif
  // router fires `wow:open-birthday` (optionally with a specific card in
  // detail) — mirror the push deep-link behaviour so the card actually opens
  // instead of only landing on Home. (User report 2026-06: the "Dom is
  // celebrating 3 years" bell didn't take anyone to the wall.)
  useEffect(() => {
    const onOpenBday = (e) => {
      const card = e?.detail?.card || null;
      resetViews();
      setShowHome(true);
      if (card && card.honoree_id) setWallCard(card);
      setShowBdayWall(true);
    };
    window.addEventListener("wow:open-birthday", onOpenBday);
    return () => window.removeEventListener("wow:open-birthday", onOpenBday);
  }, []);


  useEffect(() => {
    // Persist across app closes on personal devices — localStorage survives
    // PWA relaunches, browser restarts, and backgrounded iOS Safari. Clearing
    // happens when the user explicitly signs out OR their 1h session expires
    // (unless they ticked "Keep me signed in" at login).
    //
    // Security: require BOTH `ecDashUser` AND `ecDashSession` present and
    // well-formed before auto-restoring. Logout wipes both together, so any
    // scenario where only one exists = tampered / partial state = treat as
    // logged out. (hardened 2026-05-02 per user report "logout + refresh
    // logs me back in")
    //
    // 2026-08-31 escape hatch: BEFORE punting to the login screen, try
    // silently swapping `ecDashRefresh` for a fresh session. iOS Safari
    // wipes localStorage after ~7 days of inactivity, and PWA push-taps
    // spin up a cold context — both cases used to force a full re-login.
    // The refresh token lets us skip the sign-in screen entirely if the
    // user checked "Stays on this device," and skip straight to the PIN
    // pad (with their avatar shown) otherwise.
    const savedUser = localStorage.getItem("ecDashUser");
    const savedSession = localStorage.getItem("ecDashSession");
    const savedRefresh = localStorage.getItem("ecDashRefresh");

    const tryRefreshOrPunt = async (reason) => {
      recordLogoutReason(reason);
      // Wipe the volatile pair; keep refresh for the recovery attempt.
      try {
        localStorage.removeItem("ecDashUser");
        localStorage.removeItem("ecDashSession");
        sessionStorage.removeItem("ecDashUser");
      } catch { /* ignore */ }
      if (!savedRefresh) return; // fall through → login screen
      try {
        const r = await axios.post(`${API}/auth/refresh`, { refresh_token: savedRefresh }, { timeout: 15000 });
        const data = r?.data || {};
        if (data.soft_lock) {
          // Shared-device path — surface the quick-unlock screen (avatar
          // + PIN pad only, no Employee-ID typing). LoginPage picks this
          // up from ecDashSoftLock and skips step 1.
          try {
            localStorage.setItem("ecDashSoftLock", JSON.stringify({
              employee_id: data.employee_id || "",
              first_name: data.first_name || "",
              avatar_url: data.avatar_url || "",
              crew_id: data.crew_id || "",
              home_store_city: data.home_store_city || "",
            }));
            if (data.refresh_token) localStorage.setItem("ecDashRefresh", data.refresh_token);
          } catch { /* ignore */ }
          return;
        }
        if (data.crew_id) {
          // Silent re-auth succeeded — persist the fresh payload and
          // rebuild the session stamp exactly as LoginPage would.
          try {
            localStorage.setItem("ecDashUser", JSON.stringify(data));
            localStorage.setItem("ecDashSession", JSON.stringify({
              loginAt: Date.now(),
              deviceType: "personal",
              stayLoggedIn: true,
              authMethod: "pin",
            }));
            if (data.refresh_token) localStorage.setItem("ecDashRefresh", data.refresh_token);
          } catch { /* ignore */ }
          setUser(data);
          setShowHome(true);
        }
      } catch (e) {
        // Refresh failed (expired / revoked). Drop the stale token so
        // we don't loop, and let the login screen handle it.
        try { localStorage.removeItem("ecDashRefresh"); } catch { /* ignore */ }
      }
    };

    if (!savedUser || !savedSession) {
      tryRefreshOrPunt("session_missing_partial");
      return;
    }
    try {
      // 8h absolute session expiry (extended 2026-06-12 from 1h after
      // user reports of "random sign-outs during active shifts" —
      // managers are working 5-8 hour stretches and the 1h cap was
      // booting them mid-task when iOS Safari backgrounded the PWA and
      // re-mounted on resume). With KMSI ticked, expiry is bypassed
      // entirely. Activity also bumps `loginAt` (see the `bumpSession`
      // helper below) so a session that's been actively used keeps
      // refreshing its 8h window. If the stamp is missing/malformed we
      // treat as expired (conservative). Fresh logins always include a
      // stamp via LoginPage.
      let session = null;
      try { session = JSON.parse(savedSession); } catch (_) { session = null; }
      if (!session || typeof session.loginAt !== "number") {
        tryRefreshOrPunt("session_malformed");
        return;
      }
      // Device-type session caps (2026-06): personal = 7 days (then a
      // quick PIN unlock via the refresh soft-lock), shared = 1 hour
      // (then a full re-login). Legacy sessions without deviceType fall
      // back to their old stayLoggedIn semantics.
      const dt = session.deviceType || (session.stayLoggedIn ? "personal" : "shared");
      // Stay signed in (2026-10-02): personal devices effectively never
      // expire on the client (~10yr cap); shared devices ride a wide safety
      // cap and are handed off at end of day by the midnight-MST kick below,
      // NOT a short hard cap. (Replaces the old 1h-shared / 7d-personal caps
      // that were kicking crew mid-shift.)
      const cap = dt === "shared" ? (2 * 24 * 60 * 60 * 1000) : (3650 * 24 * 60 * 60 * 1000);
      const expired = Date.now() - session.loginAt > cap;
      if (expired) {
        // Personal → soft-lock (refresh token → PIN quick-unlock).
        // Shared → full punt (no refresh token remembered).
        tryRefreshOrPunt(dt === "shared" ? "session_expired_shared" : "session_expired_personal");
        return;
      }
      // PIN migration cutoff (v2.0.30, 2026-02-22): everyone who logged in
      // BEFORE the 4-digit PIN was rolled out has a session without
      // `authMethod === "pin"`. Force them back to login so they hit the
      // first-time PIN setup screen. One-time eviction — once they re-login
      // through the PIN flow the new session gets the flag and they stay
      // signed in normally going forward.
      if (session.authMethod !== "pin") {
        tryRefreshOrPunt("pin_migration_cutoff");
        return;
      }
      // Validate the user blob is well-formed JSON with a crew_id before
      // trusting it. Bad data → punt to login.
      let parsed = null;
      try { parsed = JSON.parse(savedUser); } catch (_) { parsed = null; }
      if (!parsed || !parsed.crew_id) {
        tryRefreshOrPunt("user_malformed");
        return;
      }
      setUser(parsed);
      // Always land on the HomePage picker on reload so a refresh doesn't dump
      // the user into their last role's screen.
      setShowHome(true);
      sessionStorage.removeItem("ecDashUser"); // legacy cleanup

      // Transferred-manager fix (2026-08-30). The cached `parsed` blob
      // is written once at login. If the user has since been moved to
      // another store (via /api/transfers/{id}/approve, which flips
      // `home_store_id` on the crew doc), our cached copy still points
      // at the OLD store. apiScope then injects the old store_id on
      // every API call, and the Zone Sheet + Manager Dashboard load
      // that store's blank sheet (or 403 if cross-store gated).
      //
      // Bootstrap fetch of the caller's own crew doc via the by-id
      // endpoint (unscoped by store) reconciles this immediately. We
      // only update if we detect drift so this is free on the happy
      // path. The `/api/crew` re-sync in the sibling effect can't do
      // this on its own because THAT list is store-scoped by apiScope
      // — Juan wouldn't appear in the OLD store's roster, so the sync
      // there silently no-ops.
      (async () => {
        try {
          const r = await axios.get(`${API}/crew/${parsed.crew_id}`);
          const fresh = r?.data || {};
          if (!fresh.id) return;
          const drifted = (
            fresh.home_store_id !== parsed.home_store_id ||
            fresh.district_id   !== parsed.district_id   ||
            !!fresh.is_manager  !== !!parsed.is_manager  ||
            !!fresh.is_district_manager !== !!parsed.is_district_manager ||
            !!fresh.is_admin    !== !!parsed.is_admin
          );
          if (!drifted) return;
          const next = {
            ...parsed,
            home_store_id: fresh.home_store_id || parsed.home_store_id,
            district_id:   fresh.district_id   || parsed.district_id,
            is_manager:    !!fresh.is_manager,
            is_district_manager: !!fresh.is_district_manager,
            is_admin:      !!fresh.is_admin,
            position:      fresh.position || parsed.position || "",
          };
          setUser(next);
          try { localStorage.setItem("ecDashUser", JSON.stringify(next)); } catch { /* ignore */ }
        } catch (_) {
          // Non-fatal: sibling `useEffect(user, crew)` catches drift too
          // once the crew list lands. This is the belt over that suspender.
        }
      })();
    } catch (e) {
      localStorage.removeItem("ecDashUser");
      localStorage.removeItem("ecDashSession");
      sessionStorage.removeItem("ecDashUser");
    }
  }, []);

  // Re-sync the cached `user` object with the authoritative is_admin /
  // is_manager flags pulled down with /api/crew. Without this, a crew
  // member who was promoted to manager/admin AFTER their last login keeps
  // a stale session that silently hides manager-only UI (e.g., the
  // "Post as Store" checkbox in compose / stories). Writes back to
  // localStorage so subsequent reloads pick up the corrected flags.
  // Root cause of the 2026-05-04 "can't post on behalf of the store" report.
  //
  // Also syncs `home_store_id` + `district_id` + `is_district_manager` so
  // older sessions (pre multi-tenant Phase 1) acquire the new tenant
  // metadata without forcing a re-login. (Fix for the Store Mirror data
  // leak — App.js needs these to drive the apiScope interceptor.)
  useEffect(() => {
    if (!user || !crew?.length) return;
    const mine = crew.find((c) => c.id === user.crew_id || c.employee_id === user.employee_id);
    if (!mine) {
      // The signed-in user isn't in the currently-scoped roster — e.g. a
      // District Manager with an EMPTY home_store_id, whose scoped crew list
      // is some OTHER store's roster. Without a resolved home store, every
      // downstream store-selection effect spins on an empty id and the app
      // FREEZES to a black screen the instant any tile is tapped (prod DM
      // Erin Parr, 2026-10-27: home_store_id="" + active_store_id=""). So we
      // still guarantee a home store here, using the same founder-store
      // fallback the drift-sync below uses. Settles in one pass (the guard
      // below is false once home_store_id is set), so no render loop.
      if (!user.home_store_id) {
        const next = { ...user, home_store_id: "store_3094" };
        setUser(next);
        try { localStorage.setItem("ecDashUser", JSON.stringify(next)); } catch { /* ignore */ }
      }
      return;
    }
    const nextIsAdmin = !!mine.is_admin;
    const nextIsManager = !!mine.is_manager;
    const nextIsDM = !!mine.is_district_manager;
    // Fallback: if the crew record lacks district_id (older imports before
    // the multi-tenant Phase 1 migration ran for this user's store), assume
    // the Mountain district — every store in production lives in
    // `district_mountain`. Without this fallback the Leaderboard sub-tabs
    // ("District · Stores" / "District · Crew") never render for sister
    // stores like 3015. (User report 2026-05-23 "3015 can only see their
    // store and not district stores, or district crew".)
    const nextDistrictId = mine.district_id || user.district_id || "district_mountain";
    const nextHomeStoreId = mine.home_store_id || "store_3094";
    const nextPosition = mine.position || user.position || "";
    if (
      nextIsAdmin === !!user.is_admin &&
      nextIsManager === !!user.is_manager &&
      nextIsDM === !!user.is_district_manager &&
      nextDistrictId === (user.district_id || "") &&
      nextHomeStoreId === (user.home_store_id || "") &&
      nextPosition === (user.position || "")
    ) {
      return; // no drift, no-op
    }
    const next = {
      ...user,
      is_admin: nextIsAdmin,
      is_manager: nextIsManager,
      is_district_manager: nextIsDM,
      district_id: nextDistrictId,
      home_store_id: nextHomeStoreId,
      position: nextPosition,
    };
    setUser(next);
    try { localStorage.setItem("ecDashUser", JSON.stringify(next)); } catch { /* ignore */ }
  }, [user, crew]);

  // Auto-detect new deploys AND admin-triggered force refreshes. Every 30s
  // (and on tab-return), we compare the baked-in package.json version against
  // `/api/version` AND check for a fresh `force_reload_at` timestamp a
  // manager may have pushed from Settings. In either case we surface a
  // sticky toast with a one-tap Reload. (user ask 2026-05-02)
  useEffect(() => {
    let alerted = false;
    const FORCE_KEY = "ecDashForceReloadAt";
    const check = async () => {
      if (alerted) return;
      try {
        // Bust browser + Cloudflare edge caches by appending a unique
        // timestamp. Without this, even a `Cache-Control: no-store`
        // response can be served stale by intermediate caches that
        // ignore the header. Combined with the server-side
        // `CDN-Cache-Control: no-store` (2026-06-15), this guarantees
        // every poll hits origin. (User report 2026-06-15 "Force
        // Update is not working in production".)
        const r = await axios.get(`${API}/version`, {
          params: { _: Date.now() },
          // Defensive: if Cloudflare returns an HTML challenge page
          // instead of JSON (5xx fallback, rate limit, etc.), don't
          // let it blow up the poll. Treat anything that isn't a
          // proper JSON body as "skip this tick".
          transformResponse: [(data) => {
            if (typeof data === "string") {
              const trimmed = data.trim();
              if (trimmed.startsWith("<")) return null; // HTML — Cloudflare or proxy
              try { return JSON.parse(trimmed); } catch { return null; }
            }
            return data;
          }],
        });
        if (!r.data || typeof r.data !== "object") {
          // Cloudflare HTML fallback or transient network blip —
          // silently skip this tick and try again next interval.
          return;
        }
        const live = r.data?.version;
        const force = r.data?.force_reload_at || "";
        const seen = localStorage.getItem(FORCE_KEY);
        // First-ever visit: adopt the server's current stamp so we don't
        // loop-reload on initial load. Only NEW stamps after this trigger a reload.
        if (seen === null) {
          localStorage.setItem(FORCE_KEY, force);
        } else if (force && force !== seen) {
          // Admin broadcast: auto-reload immediately so idle tabs refresh
          // within ~30 seconds of the manager tapping Force Update.
          localStorage.setItem(FORCE_KEY, force);
          alerted = true;
          // Honor the "Stays on this device" opt-in (LoginPage checkbox
          // `stayLoggedIn`, persisted as `ecDashStayPref==="1"`). If the
          // user explicitly asked to stay signed in across sessions, do
          // NOT wipe their auth on a deploy — just clear caches + service
          // workers and reload. Their 8-hour sliding session + token
          // restore handle the rest. (User ask 2026-06-13: "If a crew
          // member or manager is already signed in, and the remember-me
          // checkbox is selected, can we keep them signed in or
          // automatically restore their session after an app update.")
          let stayPref = false;
          try { stayPref = localStorage.getItem("ecDashStayPref") === "1"; } catch { /* default false */ }
          const toastBody = stayPref
            ? "Manager pushed an update — refreshing in a moment…"
            : "Manager pushed an update — signing you out for a fresh start…";
          toast(toastBody, { duration: 2000 });
          // Cache + SW cleanup runs unconditionally so the new bundle
          // actually loads. Auth cleanup is gated on the opt-out.
          try {
            if (!stayPref) {
              // Full logout cleanup so the force-update genuinely forces
              // a clean experience. Without this the user just gets a
              // page reload but their session + cached shell + service
              // worker survive — defeats the point when admin pushed
              // something big like the Launch Day banner that needs a
              // cold paint to appear. (Original 2026-05-26 ask.)
              ["ecDashUser", "ecDashSession", "ecDashCrew"].forEach((k) => {
                try { localStorage.removeItem(k); } catch { /* ignore */ }
                try { sessionStorage.removeItem(k); } catch { /* ignore */ }
              });
              try { sessionStorage.clear(); } catch { /* ignore */ }
            }
            // FORCE_KEY (re-set above) is preserved so this device
            // doesn't re-trigger the same broadcast on the NEXT load
            // and end up in a reload loop.
            if (typeof caches !== "undefined" && caches.keys) {
              caches.keys().then((names) => names.forEach((n) => caches.delete(n))).catch(() => {});
            }
            if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
              navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister())).catch(() => {});
            }
          } catch { /* ignore */ }
          setTimeout(() => {
            try { window.location.replace(window.location.pathname); }
            catch { window.location.reload(); }
          }, 900);
          return;
        }
        // Semver-aware compare. We only surface the update toast when the
        // SERVER build is strictly newer than our baked-in client bundle —
        // never when the client happens to be ahead (which was the 4.0.2
        // vs 4.0.0 infinite-toast loop reported 2026-05-04). Ties and
        // "client > server" states stay silent.
        if (live && pkg.version && isNewer(live, pkg.version)) {
          alerted = true;
          toast(`New version available (v${live})`, {
            description: `You're on v${pkg.version}. Tap to load the latest.`,
            action: { label: "Reload", onClick: () => window.location.reload() },
            duration: Infinity,
          });
        }
      } catch { /* offline — silent */ }
    };
    check();
    const id = setInterval(check, 30 * 1000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Poll once a minute: if the session has aged past 8h (and wasn't a
  // "Keep me signed in" login), kick the user back to the login screen.
  // Sliding expiry — activity bumps `loginAt` via the `bumpSession`
  // helper, so a manager actively using the app for their full 8h
  // shift never times out. (User report 2026-06-12 "users being
  // randomly signed out during active app use".)
  useEffect(() => {
    if (!user) return;
    const check = () => {
      let session = null;
      try {
        const raw = localStorage.getItem("ecDashSession");
        session = raw ? JSON.parse(raw) : null;
      } catch (_) { session = null; }
      if (!session) return;
      const dt = session.deviceType || (session.stayLoggedIn ? "personal" : "shared");
      // Stay signed in (2026-10-02): personal ~never, shared wide safety cap
      // (end-of-day handoff handled by the midnight-MST kick below).
      const cap = dt === "shared" ? (2 * 24 * 60 * 60 * 1000) : (3650 * 24 * 60 * 60 * 1000);
      if (Date.now() - (session.loginAt || 0) <= cap) return;
      if (dt === "shared") {
        // Shared device → hard sign-out (full Employee-ID re-login).
        recordLogoutReason("session_expired_shared_poll");
        handleLogout();
      } else {
        // Personal device → soft re-lock to the quick PIN-unlock screen.
        softLockPunt("session_expired_personal_poll");
      }
    };
    check();
    const id = setInterval(check, 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sliding session refresh — bump `loginAt` on real user activity
  // (focus, visibility, key/tap events) so an active user never times
  // out mid-shift. Refresh is throttled to once per minute to avoid
  // localStorage thrash. KMSI sessions skip the refresh (they're
  // permanent until manual logout). (User ask 2026-06-12 "active users
  // should not be randomly kicked out".)
  useEffect(() => {
    if (!user) return;
    let lastBump = 0;
    const bump = () => {
      const now = Date.now();
      if (now - lastBump < 60_000) return; // throttle
      lastBump = now;
      try {
        const raw = localStorage.getItem("ecDashSession");
        const session = raw ? JSON.parse(raw) : null;
        // No sliding refresh anymore — personal (7d) and shared (1h) are
        // both hard caps from login. Skip if the session carries a device
        // type (all sessions do post-2026-06).
        if (!session || session.stayLoggedIn || session.deviceType) return;
        session.loginAt = now;
        localStorage.setItem("ecDashSession", JSON.stringify(session));
      } catch { /* quota or parse — ignore */ }
    };
    const onFocus = () => bump();
    const onVisible = () => { if (document.visibilityState === "visible") bump(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    document.addEventListener("click", bump, { passive: true });
    document.addEventListener("keydown", bump, { passive: true });
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("click", bump);
      document.removeEventListener("keydown", bump);
    };
  }, [user]);

  // Daily midnight refresh — at 12:00 AM MST every day, force a logout
  // for everyone who did NOT opt into "Keep me signed in". KMSI users
  // stay signed in across midnight (that's the whole point of the
  // checkbox — they explicitly asked to stick). For everyone else this
  // gives them a fresh, cache-clean app shell next time they reopen.
  // (User ask 2026-05-26 launch day: "want to logout everyone at 12am
  // everyday … allows the app to refresh anything that was maybe
  // cached behind". User report 2026-05-26 follow-up: "some users are
  // being automatically logged out after check marking keep me signed
  // in" — that was this poll overriding the KMSI opt-in. Fixed by
  // gating the kick on `!session.stayLoggedIn`.)
  // Implementation: compare login date (MST) to today's date (MST). If
  // they differ AND the user didn't tick KMSI, the session crossed
  // midnight → boot them. 60s poll = ~1-minute grace past 12 AM.
  useEffect(() => {
    if (!user) return;
    const mstDate = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    const check = () => {
      let session = null;
      try {
        const raw = localStorage.getItem("ecDashSession");
        session = raw ? JSON.parse(raw) : null;
      } catch (_) { session = null; }
      if (!session?.loginAt) return;
      // 2026-06 — midnight cache-clear kick applies to SHARED devices
      // only. Personal devices ride their 7-day window (a nightly kick
      // would break the "stays 7 days" promise).
      const dtMid = session.deviceType || (session.stayLoggedIn ? "personal" : "shared");
      if (dtMid !== "shared") return;
      const todayMst = mstDate(Date.now());
      const loginMst = mstDate(session.loginAt);
      if (todayMst !== loginMst) {
        // Session crossed midnight MST — force a clean re-login.
        recordLogoutReason("midnight_refresh");
        handleLogout();
      }
    };
    check();
    const id = setInterval(check, 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Lockout poll — two independent admin toggles:
  //   • maintenance_mode  → lock EVERYONE (admin bypasses)
  //   • district_lockout  → lock everyone EXCEPT store 3094 (admin bypasses)
  // We poll /api/settings every 30s so a flip from the admin's device
  // propagates within one cycle. Two distinct behaviors:
  //   1. While someone is logged in: hard-logout the moment we see a flag
  //      that applies to them so they can't keep using stale views.
  //   2. While on the login screen: keep LoginPage visible (with its
  //      built-in maintenance banner) so admins + bypassed-store crew
  //      can still sign in. The backend _maintenance_guard hard-blocks
  //      non-bypassed login attempts with a 503 LoginPage surfaces inline.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/settings`);
        if (cancelled) return;
        const maintOn = !!r.data?.maintenance_mode;
        const districtOn = !!r.data?.district_lockout;
        const isAdmin = !!user?.is_admin;
        // Per-store app_access_enabled bypass — server returns a list of
        // store IDs that keep access during a district lockout (admin can
        // toggle this per store via Admin → Stores → Features). Replaces
        // the old hardcoded `store_3094` check so the founder can lift
        // the lockout one store at a time. (User ask 2026-05-29.)
        const bypassStores = Array.isArray(r.data?.lockout_bypass_stores)
          ? r.data.lockout_bypass_stores
          : ["store_3094"]; // safety net for older settings docs
        const storeBypass = bypassStores.includes(user?.home_store_id || "");
        // Which gate applies to this logged-in user?
        //   - maintenance_mode → admin-only bypass
        //   - district_lockout → admin OR per-store-bypass
        const blockedByMaint = maintOn && !isAdmin;
        const blockedByDistrict = districtOn && !isAdmin && !storeBypass;
        const blocking = !!user && (blockedByMaint || blockedByDistrict);
        // Surface the LoginPage "getting some updates" banner whenever
        // EITHER flag is on (pre-login users get a heads-up; the actual
        // block fires server-side at PIN verify). Tracked separately from
        // `maintenance` (which only flips when THIS user is being blocked
        // — driving the full MaintenancePage screen).
        setLockoutBanner(maintOn || districtOn);
        setPauseBannerMessage(((r.data?.pause_banner_message) || "").trim());
        if (blocking) {
          setMaintenance(true);
          // Hard-logout the in-flight session so the screen below renders
          // the maintenance page (not a half-broken dashboard with no
          // backend access). We avoid handleLogout here because that
          // triggers a full page reload, which would fight the React
          // render of the maintenance screen. Just clear local session
          // state — the next /api/settings poll re-confirms the flag.
          if (user) {
            recordLogoutReason(blockedByDistrict ? "district_locked_out" : "store_maintenance_on");
            try {
              ["ecDashUser", "ecDashSession"].forEach((k) => localStorage.removeItem(k));
            } catch { /* ignore */ }
            setUser(null);
            resetViews();
          }
        } else {
          // Maintenance turned back off — let everyone back in. They land
          // on the login screen (since their session was cleared) and can
          // sign in normally.
          if (maintenance) setMaintenance(false);
          // Clear the bypass so the next maintenance window doesn't
          // auto-skip the gate.
          if (maintenanceBypass) setMaintenanceBypass(false);
        }
      } catch { /* offline — keep last known state */ }
    };
    check();
    // Gate the 30s maintenance poll behind visibility. Skip the fetch
    // while hidden to save battery + bandwidth; we re-check the moment
    // the user comes back so the maintenance flip still applies fast.
    const id = setInterval(() => { if (!document.hidden) check(); }, 30 * 1000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Presence refresh: keep the local `crew` list's `last_active_at` values
  // fresh so the Team / Inbox / Leaderboard activity dots reflect reality
  // (green/yellow/red/grey by recency). The crew list itself is fetched
  // once on app mount; without this poll the only crew member whose dot
  // ever updates is the one whose photo/name changes. Fires immediately
  // on login (catches the user's own bump from /auth/crew-pin) and then
  // every 60s while the session is active. (User 2026-06-12.)
  //
  // NOTE: we re-fetch only the slim /api/crew list, NOT /api/crew/avatars
  // (1-2 MB). Avatars are stable across sessions; merging happens at the
  // initial fetch in the mount effect above.
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    let cancelled = false;
    const refreshPresence = async () => {
      try {
        const res = await axios.get(`${API}/crew`, { params: { active_only: true } });
        if (cancelled) return;
        // Patch only `last_active_at` (+ a couple presence-adjacent flags)
        // back into the local state. Keep photos / merged fields intact.
        const byId = new Map((res.data || []).map((c) => [c.id, c]));
        setCrew((cur) => cur.map((c) => {
          const fresh = byId.get(c.id);
          if (!fresh) return c;
          return {
            ...c,
            last_active_at: fresh.last_active_at || c.last_active_at,
            active: fresh.active,
            is_km_signed_in: fresh.is_km_signed_in,
          };
        }));
      } catch { /* swallow — next tick will retry */ }
    };
    refreshPresence();
    const id = setInterval(refreshPresence, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.crew_id]);

  // Presence heartbeat: bump MY OWN last_active_at on the server every
  // 60s while the tab is visible. Without this, the only existing
  // heartbeat was a side effect of `/api/notifications/unread-count` —
  // but most pages (Home / Inbox / Manager / District) poll a DIFFERENT
  // endpoint (`/inbox/{id}/unread-count`) that has no heartbeat. The
  // practical result was that a user who didn't sit on Crew Tally would
  // flip green → yellow → red → grey within an hour even while actively
  // using the app on other pages. (User 2026-06-15: "statuses are not
  // updating when people login... I'm still red, other active crew in
  // the inbox are yellow or red when they're live in the app".)
  //
  // We also fire one heartbeat the moment the tab regains visibility so
  // a user coming back from another app immediately shows green to peers
  // who were polling /api/crew in the background — no 60s of perceived
  // staleness.
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    let cancelled = false;
    const ping = async () => {
      if (document.hidden) return; // don't heartbeat while backgrounded
      try {
        await axios.post(`${API}/auth/heartbeat`, { crew_id: user.crew_id });
      } catch { /* swallow — next tick will retry */ }
    };
    ping(); // immediate so login → active green is instantaneous
    const id = setInterval(ping, 60 * 1000);
    const onVis = () => { if (!document.hidden) ping(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      void cancelled; // silence unused-var nag
    };
  }, [user?.crew_id]);



  // Open a live SSE connection the moment a user lands post-login. The hook
  // auto-reconnects on network drops and self-cleans on logout. Every new
  // DM/blast/checkpoint/EOD event shows up as a toast within ~100ms instead
  // of the old 30s+ email round-trip (user request 2026-05-03).
  useRealtimeNotifications({
    crewId: user?.crew_id,
    // onEvent is where per-page state (bell count, inbox list) should
    // refresh on a realtime event. We broadcast a custom DOM event so
    // any page can opt-in by listening, without App.js having to know
    // every page's internal refresh API.
    onEvent: (evt) => {
      try {
        window.dispatchEvent(new CustomEvent("wow:notification", { detail: evt }));
        // Crew-transfer events refresh the roster + (if the transfer
        // affects me) my own user doc so the app re-scopes to the new
        // home store without a manual sign-out. (Bug fix 2026-02-27 —
        // "I transferred Jessica ... but the system still isn't
        // working showing her" + "she can only see her own store".)
        if (evt?.kind === "crew_transferred") {
          try { sessionStorage.removeItem("ecDashCrew"); } catch { /* quota */ }
          (async () => {
            try {
              const [listRes, avatarsRes] = await Promise.all([
                axios.get(`${API}/crew`, { params: { active_only: true } }),
                axios.get(`${API}/crew/avatars`),
              ]);
              const avatars = avatarsRes.data || {};
              const merged = (listRes.data || []).map((c) => ({ ...c, photo_url: avatars[c.id] || "" }));
              setCrew(merged);
              try { sessionStorage.setItem("ecDashCrew", JSON.stringify(merged)); } catch { /* quota */ }
            } catch { /* non-fatal */ }
          })();
          // If the transferred crew is ME, force a re-hydrate of my
          // stored user object so `home_store_id` picks up the flip.
          if (user?.crew_id && evt.crew_id === user.crew_id) {
            (async () => {
              try {
                const me = await axios.get(`${API}/crew/${user.crew_id}`);
                if (me?.data) {
                  const updated = { ...user, ...me.data, crew_id: user.crew_id };
                  setUser(updated);
                  try { localStorage.setItem("ecDashUser", JSON.stringify(updated)); } catch { /* quota */ }
                }
              } catch { /* non-fatal */ }
            })();
          }
        }
        // 2026-08-27 — realtime avatar refresh. Bust the crew map's
        // photo_url for the changed crew member by appending `?v=<ts>`
        // so every consumer's <img> re-fetches. React re-renders any
        // Avatar component that reads from `crew` state.
        if (evt?.kind === "avatar_updated" && evt.crew_id) {
          const ver = evt.ver || String(Date.now());
          setCrew((cur) => (cur || []).map((c) => {
            if (c.id !== evt.crew_id) return c;
            const base = `/api/crew/${evt.crew_id}/avatar`;
            return { ...c, photo_url: `${base}?v=${encodeURIComponent(ver)}` };
          }));
          // If the changed crew is ME, also refresh the user object
          // so nav-bar / profile menu avatar re-render.
          if (user?.crew_id === evt.crew_id) {
            setUser((cur) => cur ? { ...cur, photo_url: `/api/crew/${evt.crew_id}/avatar?v=${encodeURIComponent(ver)}` } : cur);
          }
        }
      } catch { /* ignore */ }
    },
  });

  // Support cold-launches via `?route=inbox` or `?route=manager` query param.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const route = params.get("route");
    // Reply-from-notification deep-link (2026-02-28). When the SW
    // action `reply-open` fires, it opens the app at
    // `/inbox?reply_to={id}&reply_to_name={name}` — auto-navigate
    // straight into that DM composer so the manager just types and
    // hits send (no home-screen bounce).
    const replyTo = params.get("reply_to");
    if (replyTo && user) {
      resetViews();
      setInboxDmCrewId(replyTo);
      setShowInbox(true);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("reply_to");
        url.searchParams.delete("reply_to_name");
        window.history.replaceState({}, "", url.pathname + (url.search || ""));
      } catch { /* ignore */ }
      return;
    }
    // Notification deep-link (2026-08-31 user report "I got a message
    // notification, clicked it, and it took me to the home page not to
    // her message"). The service worker's `notificationclick` handler
    // now navigates to `/inbox?dm=<from_id>` (DM), `/inbox?group=team`
    // or `/inbox?group=managers` (blasts), `/inbox?convo=<id>` (group
    // chat), or `/inbox?msg=<id>` (mention / reaction — thread not
    // known). Parse those here and route into the right thread so a
    // tap lands the crew ON the message, not on Home.
    const notifDm    = params.get("dm");
    const notifGroup = params.get("group");     // "team" | "managers"
    const notifConvo = params.get("convo");     // group-chat conversation id
    const notifMsg   = params.get("msg");
    if (user && (notifDm || notifGroup || notifConvo || notifMsg)) {
      resetViews();
      if (notifDm) setInboxDmCrewId(notifDm);
      if (notifGroup) window.__wowInboxGroupHint = notifGroup;
      if (notifConvo) window.__wowInboxConvoHint = notifConvo;
      if (notifMsg) window.__wowInboxMsgHint = notifMsg;
      setShowInbox(true);
      try {
        const url = new URL(window.location.href);
        ["dm", "group", "convo", "msg"].forEach((k) => url.searchParams.delete(k));
        window.history.replaceState({}, "", url.pathname + (url.search || ""));
      } catch { /* ignore */ }
      return;
    }
    if (!user) return;
    if (route) {
      // Route the notification tap to the right view. IMPORTANT: this must
      // NEVER leave every view flag false — that renders a blank screen with
      // only the theme overlay (user report 2026-06-21: "tapped a notification
      // and got a blank page, we're seeing these a lot"). The root cause was
      // this handler only knew a handful of routes while pushes send many more
      // (home, zone-sheet, voice, settings, profile, ...); any unknown route
      // hit `resetViews()` and set nothing. We now delegate to the canonical
      // `handleHomePick` router for known views and fall back to Home for
      // everything else, so a tap always lands somewhere.
      const KNOWN_PICKS = new Set([
        "inbox", "settings", "reports", "profile", "team", "leaderboard",
        "contest", "calendar", "zone-sheet", "admin", "district", "system-health",
        "recognition",
      ]);
      if (route === "birthday") {
        // Overlay — keep Home underneath so the screen is never blank if the
        // active-card fetch hasn't resolved yet (or there's no active card).
        resetViews();
        setShowHome(true);
        setShowBdayWall(true);
      } else if (route === "voice") {
        handleHomePick("inbox"); // voice clips / walkie live in the inbox
      } else if (KNOWN_PICKS.has(route)) {
        handleHomePick(route);
      } else {
        // "home", "manager", "walkie", or any unrecognized route → Home.
        resetViews();
        setShowHome(true);
      }
    }
    if (!route) return;
    // Clean the URL so refreshes don't keep re-routing.
    try {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    } catch (_) { /* ignore */ }
  }, [user]);

  // Re-run the deep-link parse whenever the browser fires popstate
  // (back/forward) or a custom 'wow-url-changed' event. The service
  // worker's `client.navigate(...)` on a notification tap updates the
  // URL but doesn't fire popstate on its own, so we dispatch our own
  // event from the SW-adjacent code (see below). This keeps the
  // notification-tap → thread-open flow working when the app is
  // already open in the background. (2026-08-31)
  //
  // 2026-10-01 FIX (lock-screen push → white screen): this listener used to
  // be gated on `user` and keyed to `[user]`, so on a COLD boot (app launched
  // fresh from a locked-screen push tap) the service worker's
  // `wow-url-changed` message arrived BEFORE the session finished restoring —
  // the listener wasn't attached yet, the message was dropped, and the deep
  // link was lost (user landed nowhere / blank). We now attach on mount
  // (no user gate). `applyTargetUrl` only rewrites window.location, which is
  // safe pre-auth; the actual deep-link PARSE effect above still waits for
  // `user`, so when the session resolves it reads the URL we captured and
  // routes correctly.
  useEffect(() => {
    const rerun = () => {
      // Cheap trick — bump a state so the deep-link effect above
      // re-fires. We use setUser(u => u) so React sees a "change"
      // without actually mutating the user.
      setUser((u) => (u ? { ...u } : u));
    };
    // Service-worker `client.postMessage({type:'wow-url-changed', url})`
    // is our primary channel for notification-tap re-routing when the
    // app is already open in the background. We NO LONGER reload via
    // client.navigate() (that blanked out Android PWAs), so the SW's
    // target URL never actually landed in window.location — we apply it
    // here ourselves, then re-run the deep-link parser.
    const applyTargetUrl = (raw) => {
      if (!raw) return;
      try {
        let search = "";
        const qIdx = raw.indexOf("?");
        if (qIdx >= 0) {
          // Path form e.g. "/inbox?dm=x&msg=y" → keep the query string.
          search = raw.slice(qIdx);
        } else {
          // Bare keyword route e.g. "inbox", "home", "walkie", "manager".
          const kw = raw.replace(/^\//, "").trim();
          if (kw && kw !== "/") search = `?route=${encodeURIComponent(kw)}`;
        }
        window.history.replaceState({}, "", window.location.pathname + search);
      } catch { /* ignore */ }
    };
    const swMsgHandler = (e) => {
      if (e?.data?.type === "wow-url-changed") {
        applyTargetUrl(e.data.url);
        rerun();
      }
    };
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.addEventListener("message", swMsgHandler);
      }
    } catch { /* ignore */ }
    window.addEventListener("popstate", rerun);
    window.addEventListener("wow-url-changed", rerun);
    return () => {
      window.removeEventListener("popstate", rerun);
      window.removeEventListener("wow-url-changed", rerun);
      try {
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.removeEventListener("message", swMsgHandler);
        }
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetViews = () => {
    setShowHome(false);
    setShowInbox(false);
    setShowSettings(false);
    setShowReports(false);
    setShowProfile(false);
    setShowTeam(false);
    setShowLeaderboard(false);
    setShowContest(false);
    setShowRecognition(false);
    setRecognitionPreset("");
    setShowCalendar(false);
    setShowZoneSheet(false);
    setShowAdmin(false);
    setShowDistrict(false);
    setShowSystemHealth(false);
  };

  // Blank-screen failsafe (2026-06-21): if a signed-in user ever ends up with
  // NO active view and no full-screen overlay, default to Home. Normal
  // navigation sets the next view in the same batched update as resetViews(),
  // so the all-false state never commits — this only fires on the buggy edge
  // (e.g. an unhandled deep-link route) that would otherwise blank the screen.
  //
  // 2026-10-01 FIX (manager-dashboard "flash" + crew-tally bounce): the
  // role-based base pages (ManagerDashboard / CrewTallyPage) render ON PURPOSE
  // when every show-flag is false and user.role is "manager"/"crew". The old
  // failsafe treated that intentional state as "blank" and forced Home, so
  // tapping "Mgr Dash" (or "Collect Emails") flashed the dashboard then
  // bounced straight back Home. Only force Home when there's genuinely NO
  // view AND no role base page would render.
  useEffect(() => {
    if (!user) return;
    const anyView = showHome || showInbox || showSettings || showReports
      || showProfile || showTeam || showLeaderboard || showContest
      || showCalendar || showZoneSheet || showAdmin || showDistrict
      || showSystemHealth || showRecognition;
    const roleBaseView = user.role === "manager" || user.role === "crew";
    if (!anyView && !showBdayWall && !roleBaseView) {
      setShowHome(true);
      // Tell the Watchdog this safety net actually FIRED. These are silent
      // (no JS exception), so without this report a routing dead-end would
      // recover invisibly and nobody would ever know. (User 2026-10-01.)
      reportSelfHeal({ reason: "no-active-view", role: user.role || "", url: window.location.href });
    }
  }, [user, showHome, showInbox, showSettings, showReports, showProfile,
      showTeam, showLeaderboard, showContest, showCalendar, showZoneSheet,
      showAdmin, showDistrict, showSystemHealth, showBdayWall]);


  // Quiet usage tracking (2026-10-01) — fire one ping whenever the active
  // top-level surface changes, so the admin Usage panel can show what's
  // actually used vs dead before we cut/hide pages. Fire-and-forget.
  useEffect(() => {
    if (!user) return;
    let key = "";
    if (showBdayWall) key = "birthday-wall";
    else if (showInbox) key = "inbox";
    else if (showSettings) key = "settings";
    else if (showReports) key = "reports";
    else if (showProfile) key = "profile";
    else if (showTeam) key = "team";
    else if (showLeaderboard) key = "leaderboard";
    else if (showContest) key = "contest";
    else if (showRecognition) key = "recognition";
    else if (showCalendar) key = "calendar";
    else if (showZoneSheet) key = "zone-sheet";
    else if (showAdmin) key = "admin";
    else if (showDistrict) key = "district";
    else if (showSystemHealth) key = "system-health";
    else if (showHome) key = "home";
    else if (user.role === "manager") key = "manager-dashboard";
    else if (user.role === "crew") key = "tally";
    if (key) {
      trackView(key, {
        crewId: user.crew_id,
        storeId: viewingStoreId || user.home_store_id || user.store_id || "",
        role: user.role || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showHome, showInbox, showSettings, showReports, showProfile,
      showTeam, showLeaderboard, showContest, showCalendar, showZoneSheet,
      showAdmin, showDistrict, showSystemHealth, showBdayWall]);

  // Home slide-up sheets (Wow Crew / Leaderboards / What's Up / Contest open
  // as sheets, not full views) — track those opens too.
  useEffect(() => {
    if (!user || !homeSheetPage) return;
    trackView(homeSheetPage, {
      crewId: user.crew_id,
      storeId: viewingStoreId || user.home_store_id || user.store_id || "",
      role: user.role || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSheetPage, user]);

  const handleLogin = (data) => {
    const { initialView, ...userData } = data;
    setUser(userData);
    // Optimistic presence: bump my OWN last_active_at to right-now so the
    // Team / Inbox / Leaderboard dots flip to green immediately instead of
    // waiting for the next /api/crew poll. The server already wrote this
    // timestamp inside /auth/crew-pin; we're just mirroring it locally so
    // the UI doesn't show the user as yellow/offline for the first few
    // seconds. (User report 2026-06-12: "the moment I sign in its not
    // updating my status across the app, the wow crew tab shows me
    // yellow, the inbox tab shows I'm inactive".)
    if (userData?.crew_id) {
      const nowIso = new Date().toISOString();
      setCrew((cur) => cur.map((c) =>
        c.id === userData.crew_id ? { ...c, last_active_at: nowIso } : c,
      ));
    }
    resetViews();
    // Reset the maintenance bypass — the next maintenance window starts
    // fresh and re-locks logged-out crew until an admin re-taps the escape.
    setMaintenanceBypass(false);

    // Welcome splash — first login per crew_id per MST day. Keyed by
    // both crew_id and date so switching users on a shared device still
    // pops a splash for each person's first login of the day, and so
    // the flag naturally rolls over at midnight without cleanup.
    // (User ask 2026-02: "once wow crew logs in it's just boring, it's
    // not fun like when they logout" — parity with the goodbye splash.)
    let welcomeWillShow = false;
    // RETIRED 2026-06-21: the animated WelcomeSplash is gone — the single
    // DailyBriefing pop-up is now the ONLY thing shown on sign-in (user:
    // "you still have two pop-ups on login, I thought we retired the
    // welcome for the briefing"). setWelcome intentionally never armed.
    // First-login tutorial prompt — once per device, only when the user
    // lands on the home grid (skip when initialView jumps straight into
    // a sub-page like inbox/manager, which means they've used the app
    // before and a deep-link surfaced them). The prompt itself decides
    // whether to actually open the tour.
    //
    // 2026-05-26 fix: defer the prompt while the Launch Day splash is
    // about to fire so the tutorial card doesn't bury the splash. We
    // detect the splash by checking today's MST date against the same
    // localStorage flag the splash uses (`ecDashLaunchSplash`). If
    // today IS a launch day AND the splash hasn't been shown yet, we
    // wait for the splash's dismiss event (`wow:launch-splash-done`,
    // fired by LaunchDayHero on tap or auto-timeout) before flipping
    // the prompt on. Falls back to a hard 4s timeout if for whatever
    // reason the event never arrives.
    // RETIRED 2026-06-21: the auto first-login welcome/tour prompt is gone.
    // The single DailyBriefing pop-up (mounted at the app root) is now the
    // one-and-only sign-in interruption. The tutorial carousel is still
    // available on demand via Settings → Replay ("showTutorial").
    void welcomeWillShow;
    if (initialView === "inbox") setShowInbox(true);
    else if (initialView === "settings") setShowSettings(true);
    else if (initialView === "reports") setShowReports(true);
    else if (initialView === "profile") setShowProfile(true);
    else if (initialView === "team") setShowTeam(true);
    else if (initialView === "leaderboard") setShowLeaderboard(true);
    else if (initialView === "contest") setShowContest(true);
    else if (initialView === "calendar") setShowCalendar(true);
    else if (initialView === "admin") setShowAdmin(true);
    else if (initialView === "district") setShowDistrict(true);
    // No specific deep-link → land on the HomePage tile picker, NOT the
    // role-based default page. (User report 2026-05-26: "when you type
    // your pin and login it's taking you right to manager dashboard —
    // should go to the Home Screen.") Without this, the App falls through
    // to ManagerDashboard / CrewTallyPage based on user.role and skips
    // the new modernized tile grid entirely.
    else setShowHome(true);
    localStorage.setItem("ecDashUser", JSON.stringify(userData));
  };

  const handleLogout = () => {
    // Goodbye splash (2026-05-25 user ask "on logout show something like
    // Bye, Dom! Have a great morning/evening/night ... then flash out
    // like the TV turned off"). When invoked from the slide-out panel,
    // first show the splash for ~2.2s, then run the real logout. If
    // there's no user (auth expiry, etc.) just logout immediately.
    if (user?.first_name && !goodbye) {
      const hr = new Date().getHours();
      const tod = hr < 12 ? "morning" : hr < 17 ? "afternoon" : hr < 21 ? "evening" : "night";
      setGoodbye({ name: user.first_name, tod, phase: "in" });
      // Trigger CRT-collapse animation at +1700ms, real logout at +2400ms.
      setTimeout(() => setGoodbye((g) => g ? { ...g, phase: "off" } : g), 1700);
      setTimeout(() => {
        setGoodbye(null);
        _doLogout();
      }, 2400);
      return;
    }
    _doLogout();
  };

  const _doLogout = () => {
    // Tell the server this user is signing out so the Team tree can drop their
    // green dot to grey instantly.
    const crewId = user?.crew_id;
    if (crewId) {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`;
        const body = JSON.stringify({ crew_id: crewId });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        } else {
          fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
        }
      } catch (_) { /* best effort */ }
    }
    // Wipe client storage + all caches + unregister the service worker so the
    // next login can't be served a stale shell. This fixes a report where
    // logging out and signing back in showed an older version of the app
    // because the SW was serving cached index.html. (security fix 2026-05-02)
    //
    // Fire-and-forget the server-side refresh-token revocation before we
    // wipe our copy — otherwise a stolen phone with a duplicated
    // localStorage could still refresh into the account. Failure here is
    // non-fatal; the token TTL eventually catches it either way.
    try {
      const rt = localStorage.getItem("ecDashRefresh");
      if (rt) {
        axios.post(`${API}/auth/refresh/revoke`, { refresh_token: rt }).catch(() => {});
      }
    } catch { /* ignore */ }
    try {
      ["ecDashUser", "ecDashSession", "ecDashCrew", "ecDashForceReloadAt", "ecDashRefresh", "ecDashSoftLock"].forEach((k) => {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
        try { sessionStorage.removeItem(k); } catch { /* ignore */ }
      });
      try { sessionStorage.clear(); } catch { /* ignore */ }
      // Drop every CacheStorage entry — the SW's runtime + precache caches all live here.
      if (typeof caches !== "undefined" && caches.keys) {
        caches.keys().then((names) => names.forEach((n) => caches.delete(n))).catch(() => {});
      }
      // Unregister any active service worker so the next request hits the network.
      if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
        navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister())).catch(() => {});
      }
    } catch { /* ignore */ }
    setUser(null);
    resetViews();
    // Explicitly clear mirror state too — even though the window.location
    // reload below wipes React state, this is belt-and-braces in case the
    // reload is delayed or interrupted on a slow connection. Prevents the
    // amber banner from briefly flashing on the login screen during the
    // reload race.
    setViewingStoreId("");
    setViewingStore(null);
    setViewingAsDM(false);
    // Force a full reload so React state, caches, and any in-flight API calls
    // are terminated cleanly. `replace` so the back button can't return.
    try {
      window.location.replace(window.location.pathname);
    } catch {
      window.location.href = "/";
    }
  };

  const handleSwitchRole = (newRole) => {
    if (user?.is_manager) {
      const updated = { ...user, role: newRole };
      setUser(updated);
      resetViews();
      localStorage.setItem("ecDashUser", JSON.stringify(updated));
    }
  };

  const goHome = () => {
    resetViews();
    setShowHome(true);
    // IMPORTANT: while mirroring, goHome() must KEEP mirror state active.
    // Tapping the home icon from a sub-page should land on the MIRRORED
    // store's home (banner still visible), not boot the admin out of
    // mirror mode. The explicit "Return to your store →" button on the
    // banner is the ONLY way to exit mirror — that's the whole point of
    // the mode: to make edits + investigate issues without getting
    // logged out on every navigation. (User feedback 2026-05-22: "we
    // can't do anything if [keeping mirror across navigation] isn't a
    // feature — that was the whole point in mirror".)
    //
    // Logout still clears mirror state explicitly in handleLogout below
    // — that's a different flow.
  };

  // Home picker actions
  const handleHomePick = (key, meta = {}) => {
    resetViews();
    if (key === "tally") handleSwitchRole("crew");
    else if (key === "manager") handleSwitchRole("manager");
    else if (key === "inbox") {
      // `meta.initialDmCrewId` lets callers (e.g. the founder profile card
      // "Message Dom" button) open the Inbox already focused on a thread.
      if (meta && meta.initialDmCrewId) setInboxDmCrewId(meta.initialDmCrewId);
      // `meta.group` / `meta.msgId` (e.g. a calendar signup-sheet badge) open
      // the Inbox focused on a group thread and, when possible, the message.
      try {
        if (meta && meta.group) window.__wowInboxGroupHint = meta.group;
        if (meta && meta.msgId) window.__wowInboxMsgHint = meta.msgId;
      } catch { /* ignore */ }
      setShowInbox(true);
    }
    else if (key === "settings") setShowSettings(true);
    else if (key === "reports") {
      // `meta.tab` lets the What's New "Full log" link deep-link to the
      // Changelog tab. We explicitly reset on every open (instead of
      // only setting when present) so a bare `onPick("reports")` after
      // a deep-link doesn't inherit the previous tab. Falls back to
      // ReportsPage's default ("recap"). (Test agent hardening 2026-02-14.)
      setInitialReportsTab((meta && meta.tab) || "");
      setShowReports(true);
    }
    else if (key === "profile") setShowProfile(true);
    else if (key === "team") setShowTeam(true);
    else if (key === "leaderboard") setShowLeaderboard(true);
    else if (key === "contest") setShowContest(true);
    else if (key === "recognition") {
      if (meta && meta.crewId) setRecognitionPreset(meta.crewId);
      setShowRecognition(true);
    }
    else if (key === "calendar") setShowCalendar(true);
    else if (key === "zone-sheet") {
      // `meta.date` lets the Reports → Zone Sheets tab deep-link into
      // a specific historical day. Cleared on every open so a bare
      // pick lands on today.
      setZoneSheetInitialDate((meta && meta.date) || "");
      setShowZoneSheet(true);
    }
    else if (key === "admin") setShowAdmin(true);
    else if (key === "district") setShowDistrict(true);
    else if (key === "system-health") setShowSystemHealth(true);
    // Persist last pick so the auth boot flow can restore it on the next
    // session. The lookup table lives in LoginPage.js / handleLogin in App.
    try { localStorage.setItem("ecDashLastPick", key); } catch { /* ignore */ }
  };

  // Re-fetch crew list after a profile photo/name update so avatars refresh everywhere.
  const refreshCrew = async () => {
    try {
      const [listRes, avatarsRes] = await Promise.all([
        axios.get(`${API}/crew`, { params: { active_only: true } }),
        axios.get(`${API}/crew/avatars`),
      ]);
      const avatars = avatarsRes.data || {};
      const merged = (listRes.data || []).map((c) => ({
        ...c,
        photo_url: avatars[c.id] || "",
      }));
      setCrew(merged);
      try { sessionStorage.setItem("ecDashCrew", JSON.stringify(merged)); } catch { /* quota */ }
    } catch (e) { console.error(e); }
  };

  // Screenshot-attempt detection — desktop only. Logs PrintScreen, Cmd+Shift+3/4/5,
  // and Win+Shift+S to /api/audit/screenshot-attempt for any signed-in user.
  // Browser can't detect mobile-OS screenshots (no API exposed on iOS/Android web).
  // (User ask 2026-05-21.)
  useScreenshotDetect(user?.crew_id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="app-root">
      <Toaster position="bottom-right" richColors />
      <UpdateManager />
      <SuspensionAppealModal user={user} />
      {/* Urgent coverage/help alert banner — mounted at the root so a
          priority alert pops on ANY screen with a chime + vibration and
          one-tap "I can take it" reply. (User ask 2026-06-13.) */}
      {user && <UrgentAlertBanner user={user} />}
      {user && (
        <RecognitionCelebration
          user={viewUser}
          onExplore={() => { resetViews(); setShowRecognition(true); }}
        />
      )}
      {user && (
        <DailyBriefing
          user={user}
          birthday={bdayCard}
          onGoBirthday={() => { resetViews(); setShowBdayWall(true); }}
          onGoTasks={() => { resetViews(); setShowHome(true); }}
          onGoAnnouncements={() => { resetViews(); try { window.__wowInboxGroupHint = "announcements"; } catch { /* ignore */ } setShowInbox(true); }}
          onGoInbox={() => { resetViews(); setShowInbox(true); }}
        />
      )}
      {user && showBdayWall && (wallCard || bdayCard) && (
        <BirthdayWall
          honoreeId={(wallCard || bdayCard).honoree_id}
          date={(wallCard || bdayCard).date}
          kind={(wallCard || bdayCard).kind}
          years={(wallCard || bdayCard).years}
          honoreeName={(wallCard || bdayCard).first_name || (wallCard || bdayCard).honoree_name || ""}
          user={user}
          onClose={() => { setShowBdayWall(false); setWallCard(null); setBelatedNonce((n) => n + 1); goHome(); }}
        />
      )}
      {/* Spider-Man theme overlay — mounted once at the app root so
          all pages get the web corners + easter-egg triggers. Renders
          nothing when the theme isn't active. */}
      <SpiderManTheme active={isSpiderTheme} />

      {/* Halloween atmosphere overlay — moon glow + cobwebs + drifting
          bats + fog. Suppressed inside LP so nothing floats over the
          secure module. */}
      <HalloweenTheme active={isHalloweenTheme} />

      {/* Pitch Black (Pokémon event) overlay — neon lightning shards +
          Darkrai wisp + electric flash. Renders nothing when inactive. */}
      <PitchBlackTheme active={isPitchBlackTheme} />

      {/* Pitch Black floating glass dock — quick nav shortcut. Hidden in
          the Inbox (its own composer owns the bottom) and pre-login.
          Drives the same handlers the Home tiles use. */}
      <PitchBlackDock
        items={(isPitchBlackTheme && user && !showInbox) ? [
          { key: "home", label: "Home", Icon: PBHouse, active: showHome, onClick: goHome },
          { key: "zone", label: "Zone", Icon: PBClipboard, active: showZoneSheet, onClick: () => handleHomePick("zone-sheet") },
          { key: "inbox", label: "Inbox", Icon: PBChat, active: false, onClick: () => handleHomePick("inbox") },
          ...((user?.is_admin || user?.is_manager || user?.is_district_manager)
            ? [{ key: "reports", label: "Reports", Icon: PBChart, active: showReports, onClick: () => handleHomePick("reports") }]
            : []),
        ] : []}
      />

      {/* Goodbye splash — see `handleLogout` for sequencing. CRT-off
          collapse + fade. Lives in the root so it overlays every screen. */}
      {goodbye && (
        <GoodbyeSplash name={goodbye.name} tod={goodbye.tod} phase={goodbye.phase} />
      )}

      {/* Welcome splash — mirror of the goodbye. Fires once per crew_id
          per MST day on login. Time-of-day art + rotating greeting +
          yesterday's stat brag + confetti. Dismisses on tap or after
          ~1.8s and broadcasts `wow:welcome-splash-done` so the
          first-login tutorial prompt waits its turn. */}
      {welcome && (
        <WelcomeSplash
          user={welcome}
          onDismiss={() => {
            setWelcome(null);
            try { window.dispatchEvent(new CustomEvent("wow:welcome-splash-done")); }
            catch { /* ignore */ }
          }}
        />
      )}

      {/* One-tap "Cover {code}" swap pill (2026-02) — surfaces when an
          admin/DM has recently mirrored another store (< 24h) and is
          currently back on their home store. Solves the "switching back
          and forth is painful" report: instead of Admin console → find
          store → mirror (4-5 taps), it's now one tap. Hides itself
          during active mirror (the yellow banner + Return already
          handle that direction). Dismissable per-day via the small ×. */}
      <StoreSwapToggle
        user={user}
        viewingStoreId={viewingStoreId}
        onSwapTo={(store) => {
          setViewingStoreId(store.id);
          setViewingStore(store);
          saveLastCoveredStore(user?.crew_id, store);
          setShowHome(true);
        }}
      />

      {/* "Viewing Store X" mirror banner — sticky at the top whenever
          admin/DM has opened a sister store from the Admin console. Tap
          "Return" to exit mirror mode. Hidden when no store override is
          active. (User ask 2026-05-19: admin clicks on a store, app
          mirrors that store's full experience.) */}
      {viewingStoreId && viewingStore && (
        <div
          className="fixed top-0 left-0 right-0 z-[70] bg-amber-500 text-amber-950 text-xs font-bold flex items-center justify-between gap-3 px-3 py-1.5 shadow-md"
          data-testid="viewing-store-banner"
        >
          <span className="truncate">
            👀 Viewing <span className="font-black">{viewingStore.code} · {viewingStore.name}</span>
          </span>
          <button
            type="button"
            onClick={() => { setViewingStoreId(""); setViewingStore(null); }}
            className="rounded-full bg-amber-950 text-amber-50 px-3 py-0.5 hover:bg-amber-900 transition-colors flex-shrink-0"
            data-testid="viewing-store-return"
          >
            Return to your store →
          </button>
        </div>
      )}
      {/* "Viewing as DM" banner — same pattern as the store mirror banner
          but for the DM persona. Lets the admin always exit DM-view mode
          cleanly without having to know the keyboard sequence to undo it. */}
      {viewingAsDM && !viewingStoreId && (
        <div
          className="fixed top-0 left-0 right-0 z-[70] bg-violet-500 text-violet-50 text-xs font-bold flex items-center justify-between gap-3 px-3 py-1.5 shadow-md"
          data-testid="viewing-dm-banner"
        >
          <span className="truncate">
            👀 Viewing as <span className="font-black">District Manager</span>
          </span>
          <button
            type="button"
            onClick={() => { setViewingAsDM(false); setShowDistrict(false); setShowHome(true); }}
            className="rounded-full bg-violet-900 text-violet-50 px-3 py-0.5 hover:bg-violet-800 transition-colors flex-shrink-0"
            data-testid="viewing-dm-return"
          >
            Exit DM view →
          </button>
        </div>
      )}
      <div style={(viewingStoreId || viewingAsDM) ? { paddingTop: 30 } : undefined}>

      {/* Maintenance gate.
          - Logged-in non-admin → MaintenancePage (lockout, no escape).
          - Logged-out users    → MaintenancePage with hidden 5-tap escape
            (so admins still have a way in). Crew/managers never see the
            login form during maintenance, only the static "we'll be back"
            screen. Backend `_maintenance_guard` also rejects non-admin
            logins, so even if a crew member found the hidden escape they
            still couldn't get a token.
          - Admins (signed in) → fall through to normal app.
          - `maintenanceBypass` flips true via the 5-tap escape so the
            LoginPage renders with the maintenance banner. (User ask 2026-02:
            "the login shouldn't even show only the message should appear,
            admin will see the login page with the message".) */}
      {maintenance && user && !user.is_admin ? (
        <MaintenancePage message={pauseBannerMessage} onAdminSignIn={() => {
          // Hard-logout this non-admin session so the LoginPage renders.
          // The backend's _maintenance_guard rejects non-admin auth attempts
          // with 503, so this is safe — only admins will actually get a
          // token from the resulting login flow.
          try {
            ["ecDashUser", "ecDashSession"].forEach((k) => localStorage.removeItem(k));
          } catch { /* ignore */ }
          setUser(null);
          resetViews();
          setMaintenanceBypass(true);
        }} />
      ) : maintenance && !user && !maintenanceBypass ? (
        <MaintenancePage message={pauseBannerMessage} onAdminSignIn={() => setMaintenanceBypass(true)} />
      ) : (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="page-suspense-fallback">
          <div className="animate-pulse text-sm font-semibold uppercase tracking-widest text-slate-400">Loading…</div>
        </div>
      }>
      <>
      {!user && (
        <LoginPage onLogin={handleLogin} crew={crew} maintenanceOn={lockoutBanner} pauseBannerMessage={pauseBannerMessage} />
      )}

      {/* PIN reset request inbox banner — only renders for managers/admins/DM
          when one or more pending requests exist at their store. Self-mounts
          as `position: fixed` at the top of the screen so it overlays any
          page they're currently on. (User 2026-06-12.) */}
      {user && <PinResetRequestsBanner user={user} />}

      {user && showHome && useLegacyHome && (
        <HomePage user={viewUser} crew={crew} onPick={handleHomePick} onSwitchUser={handleLogout} onOpenV2Preview={() => { /* V2 is default now — legacy param disables it */ }} />
      )}

      {user && showHome && !useLegacyHome && (
        <HomeScreen
          user={viewUser}
          crew={crew}
          birthday={bdayCard}
          onOpenBirthday={() => { resetViews(); setShowBdayWall(true); }}
          belatedCards={belatedCards}
          onOpenBelated={(card) => { setWallCard(card); resetViews(); setShowHome(true); setShowBdayWall(true); }}
          onOpenKeepsake={(card) => { setWallCard(card); resetViews(); setShowHome(true); setShowBdayWall(true); }}
          onSwitchUser={handleLogout}
          onNavigate={(route, meta) => {
            const alias = { "manager-dashboard": "manager", leaderboards: "leaderboard", "whats-up": "calendar" };
            handleHomePick(alias[route] || route, meta);
          }}
          onOpenSheet={(pageKey) => setHomeSheetPage(pageKey)}
        />
      )}

      {/* Home pop-out overlays — Wow Crew, Leaderboards, What's Up, and
          Contest open as centered modal cards (same shape as Penny
          Rounding). Backdrop click, Escape, and each page's own back
          button all close the overlay back onto Home. `key={homeSheetPage}`
          forces a fresh page mount per open so state doesn't leak
          between overlays. */}
      <Dialog open={!!homeSheetPage} onOpenChange={(o) => { if (!o) closeHomeSheet(); }}>
        <DialogContent
          hideClose
          className="p-0 gap-0 max-w-2xl w-[calc(100%-16px)] max-h-[92vh] overflow-hidden rounded-3xl border-slate-200 shadow-2xl"
          data-testid={`home-sheet-${homeSheetPage || "none"}`}
        >
          {/* Visually-hidden title for screen readers + Radix a11y warning
              (each Dialog must expose a DialogTitle). The inner page renders
              its own visible header. */}
          <DialogTitle className="sr-only">
            {homeSheetPage === "team" ? "Wow Crew"
              : homeSheetPage === "leaderboard" ? "Leaderboards"
              : homeSheetPage === "contest" ? "Weekly Contest"
              : homeSheetPage === "calendar" ? "Calendar"
              : "Home"}
          </DialogTitle>
          <div key={homeSheetPage} className="flex-1 overflow-y-auto max-h-[92vh]">
            {homeSheetPage === "team" && (
              <TeamPage
                user={viewUser}
                crew={crew}
                onMount={refreshCrew}
                onBack={closeHomeSheet}
                onGoHome={closeHomeSheet}
                onMessage={(crewId) => {
                  setInboxDmCrewId(crewId);
                  closeHomeSheet();
                  resetViews();
                  setShowInbox(true);
                }}
                onHighFive={(crewId) => { closeHomeSheet(); handleHomePick("recognition", { crewId }); }}
                onGoToCalendar={() => setHomeSheetPage("calendar")}
              />
            )}
            {homeSheetPage === "leaderboard" && (
              <LeaderboardPage user={user} onBack={closeHomeSheet} onGoHome={closeHomeSheet} />
            )}
            {homeSheetPage === "contest" && (
              <ContestPage user={user} onBack={closeHomeSheet} onGoHome={closeHomeSheet} />
            )}
            {homeSheetPage === "calendar" && (
              <CalendarPage
                user={user}
                onBack={closeHomeSheet}
                onGoHome={closeHomeSheet}
                onPick={(k, meta) => { closeHomeSheet(); handleHomePick(k, meta); }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lazy loading removed — all pages import eagerly so navigation is
          instant (user report 2026-05-02 "everything feels lazy"). The 200KB
          initial bundle is fine on any modern device. */}
      {user && !showHome && showProfile && (
        <ProfilePage user={user} onBack={goHome} onGoHome={goHome} onUpdated={refreshCrew} onOpenKeepsake={(card) => { setWallCard(card); setShowBdayWall(true); }} />
      )}

      {user && !showHome && showTeam && !showProfile && !showInbox && !showSettings && !showReports && !showContest && !showCalendar && !showZoneSheet && !showLeaderboard && !showAdmin && !showDistrict && (
        <TeamPage
          user={viewUser}
          crew={crew}
          onMount={refreshCrew}
          onBack={goHome}
          onGoHome={goHome}
          onMessage={(crewId) => {
            setInboxDmCrewId(crewId);
            resetViews();
            setShowInbox(true);
          }}
          onHighFive={(crewId) => handleHomePick("recognition", { crewId })}
          onGoToCalendar={() => { resetViews(); setShowCalendar(true); }}
        />
      )}

      {user && !showHome && !showZoneSheet && showInbox && !showProfile && (
        <InboxPage
          user={viewUser}
          crew={crew}
          onBack={() => { setInboxDmCrewId(""); goHome(); }}
          onGoHome={() => { setInboxDmCrewId(""); goHome(); }}
          initialDmCrewId={inboxDmCrewId}
        />
      )}

      {user && !showHome && showLeaderboard && !showInbox && !showSettings && !showReports && !showProfile && !showTeam && !showContest && !showCalendar && !showZoneSheet && (
        <LeaderboardPage user={user} onBack={goHome} onGoHome={goHome} />
      )}

      {user && !showHome && showContest && !showInbox && !showSettings && !showReports && !showProfile && !showTeam && !showLeaderboard && !showCalendar && !showZoneSheet && (
        <ContestPage user={user} onBack={goHome} onGoHome={goHome} />
      )}

      {user && !showHome && showRecognition && !showInbox && !showSettings && !showReports && !showProfile && !showTeam && !showLeaderboard && !showContest && !showCalendar && !showZoneSheet && (
        <RecognitionPage user={viewUser} crew={crew} presetRecipientId={recognitionPreset} onBack={goHome} onGoHome={goHome} />
      )}

      {user && !showHome && showCalendar && !showInbox && !showSettings && !showReports && !showProfile && !showTeam && !showLeaderboard && !showContest && (
        <CalendarPage user={user} onBack={goHome} onGoHome={goHome} onPick={handleHomePick} />
      )}

      {user && !showHome && showZoneSheet && (
        /* Pass viewUser (not raw user) so admin mirroring scopes ALL data
           — Zone Sheet, tasks, crew, photos — to the mirrored store. Raw
           `user` here was the root cause of the 2026-06-12 store-data
           leak (admin mirroring Littleton saw Dom's 3094 assignments).
           viewUser preserves crew_id for actor_id audit while flipping
           home_store_id to the target store. */
        <ZoneSheetPage user={viewUser} crewList={crew} onBack={goHome} onNavigate={handleHomePick} initialDate={zoneSheetInitialDate} />
      )}

      {/* SettingsPage was previously passed raw `user` here which made admin
          mirroring break crew CRUD — `user.home_store_id` stayed at Dom's
          real home (3094) instead of the mirrored store. Switched to
          `viewUser` 2026-05-22 so addCrew/branding writes land in the
          right tenant. The same fix benefits real DMs who set
          `home_store_id` to a particular store via mirror; viewUser
          preserves crew_id (admin's true identity) for actor_id audit
          while flipping store identity. */}
      {user && !showHome && showSettings && !showInbox && !showReports && !showProfile && !showTeam && !showLeaderboard && !showContest && !showCalendar && !showZoneSheet && !showAdmin && !showDistrict && (
        <SettingsPage user={viewUser} crewList={crew} onBack={goHome} onGoHome={goHome} />
      )}

      {user?.is_admin && !showHome && !showZoneSheet && showAdmin && (
        <AdminPage
          user={user}
          onBack={goHome}
          onGoHome={goHome}
          onOpenSystemHealth={() => {
            setShowAdmin(false);
            setShowSystemHealth(true);
          }}
          onOpenInbox={(meta) => handleHomePick("inbox", meta)}
          onViewAsStore={(store) => {
            // Enter mirror mode + jump to home so admin sees the target
            // store's full home grid + tiles. We deliberately skip the
            // toast notification here — the persistent yellow banner at the
            // top already announces the mirror state, and the toast was
            // intercepting taps on the banner's "Return" button for its
            // ~3s lifetime (user report 2026-02: "the yellow banner is
            // staying once you exit mirror mode" — they were tapping
            // Return while the toast still covered it).
            setViewingStoreId(store.id);
            setViewingStore(store);
            saveLastCoveredStore(user?.crew_id, store);
            setShowAdmin(false);
            setShowHome(true);
          }}
          onViewAsDM={() => {
            // "View as District Manager" — opens the cross-store DM
            // dashboard. Admin already has DM-superset permissions on the
            // backend; this is just a UI shortcut so the founder can see
            // exactly what their DM sees without role-swapping. Setting
            // `viewingAsDM` also flips `viewUser` so HomePage renders the
            // DM tile filter when admin taps Home from the dashboard.
            setShowAdmin(false);
            setViewingAsDM(true);
            setShowDistrict(true);
            toast.success("Viewing as District Manager");
          }}
        />
      )}

      {(user?.is_admin || user?.is_district_manager || user?.is_training_sm) && !showHome && !showZoneSheet && showDistrict && (
        <DistrictDashboard
          user={viewUser}
          crew={crew}
          onBack={goHome}
          onGoHome={goHome}
          onPick={handleHomePick}
          onOpenKeepsake={(card) => { setWallCard(card); resetViews(); setShowHome(true); setShowBdayWall(true); }}
          onLogout={handleLogout}
          onViewAsStore={(store) => {
            // From the DM Dashboard → tap a store card → enter Store
            // Mirror mode just like the Admin console does. Skip the toast
            // (banner is the source of truth + it was blocking Return).
            setViewingStoreId(store.id);
            setViewingStore(store);
            saveLastCoveredStore(user?.crew_id, store);
            setShowDistrict(false);
            setShowHome(true);
          }}
        />
      )}

      {(user?.is_admin || user?.is_manager || user?.is_district_manager) && !showHome && showReports && !showInbox && !showProfile && !showTeam && !showLeaderboard && !showContest && !showCalendar && !showZoneSheet && (
        <ReportsPage
          user={viewUser}
          crewList={crew}
          onBack={() => { setShowReports(false); setInitialReportsTab(""); goHome(); }}
          onGoHome={() => { setShowReports(false); setInitialReportsTab(""); goHome(); }}
          initialTab={initialReportsTab}
          onPick={(key, meta) => { setShowReports(false); setInitialReportsTab(""); handleHomePick(key, meta); }}
        />
      )}

      {user?.role === "crew" && !showHome && !showInbox && !showSettings && !showReports && !showProfile && !showTeam && !showLeaderboard && !showContest && !showCalendar && !showZoneSheet && !showAdmin && !showDistrict && !showSystemHealth && !showRecognition && (
        <CrewTallyPage
          user={viewUser}
          crew={crew}
          onLogout={handleLogout}
          onSwitchToManager={user?.is_manager ? () => handleSwitchRole("manager") : null}
          onOpenInbox={() => setShowInbox(true)}
          onGoHome={goHome}
        />
      )}

      {user?.role === "manager" && !showHome && !showInbox && !showSettings && !showReports && !showProfile && !showTeam && !showLeaderboard && !showContest && !showCalendar && !showZoneSheet && !showAdmin && !showDistrict && !showSystemHealth && !showRecognition && (
        <ManagerDashboard
          user={viewUser}
          onLogout={handleLogout}
          onSwitchToTally={() => handleSwitchRole("crew")}
          onOpenInbox={() => setShowInbox(true)}
          onOpenSettings={() => setShowSettings(true)}
          onOpenReports={(opts) => {
            // ProfileMenuButton's WhatsNew popover passes { tab: "changelog" }
            // so "Full log" deep-links to the Changelog tab inside Reports.
            // Reset explicitly on every open so a subsequent bare call
            // doesn't inherit the previous deep-link target.
            setInitialReportsTab((opts && opts.tab) || "");
            setShowReports(true);
          }}
          onGoHome={goHome}
          onNavigate={handleHomePick}
        />
      )}

      {user?.is_admin && showSystemHealth && (
        <SystemHealthPage
          user={user}
          onBack={() => { setShowSystemHealth(false); goHome(); }}
          onGoHome={goHome}
        />
      )}
      </>
      </Suspense>
      )}
      </div>
      {/* Tutorial overlays — only render once a user is signed in, and
          mount above maintenance + everything else (z-index inside the
          components handles stacking). The welcome prompt asks first;
          the actual carousel mounts when the user accepts or replays. */}
      {user && showWelcomePrompt && (
        <WelcomePrompt
          user={user}
          onStart={() => { setShowWelcomePrompt(false); setShowTutorial(true); }}
          onSkip={() => setShowWelcomePrompt(false)}
        />
      )}
      {user && showTutorial && (
        <TutorialOverlay user={user} onClose={() => setShowTutorial(false)} />
      )}
      {user && (
        <GlobalTransferRequestGate user={user} />
      )}
    </div>
  );
}

function AppRouter() {
  // Print page route — auth gating happens INSIDE ZoneSheetPrintPage
  // via useEffect (see that file). Doing it here pre-mount caused a
  // regression where new-tab opens (window.open(url, "_blank",
  // "noopener")) saw an empty localStorage during the brief tab-init
  // window, even when the parent tab had a valid session — booting
  // authenticated users back to login.
  //
  // The in-component check is fail-closed (component renders a
  // "Redirecting to login…" placeholder while the redirect fires)
  // AND stashes `postLoginRedirect` so a deep-link user lands back on
  // the print page after auth. Security guarantee preserved: anyone
  // without a session in localStorage gets bounced to login before
  // any Zone Sheet content renders. (P0 regression fix 2026-06-16.)
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("route") === "zone-sheet-print") {
      return <ZoneSheetPrintPage />;
    }
  }
  return <App />;
}

// Global bridge: any page can dispatch `wow:open-transfer-request` with
// `{ crew }` in the event detail and this gate opens the sheet with
// the current user as the actor. Wired here so managers can request
// transfers from Manager Dashboard, Settings, or anywhere else without
// each screen needing to embed the sheet. (2026-02-27)
function GlobalTransferRequestGate({ user }) {
  const [openFor, setOpenFor] = useState(null);
  useEffect(() => {
    const onOpen = (e) => {
      const c = e?.detail?.crew;
      if (c) setOpenFor(c);
    };
    window.addEventListener("wow:open-transfer-request", onOpen);
    return () => window.removeEventListener("wow:open-transfer-request", onOpen);
  }, []);
  return (
    <TransferRequestSheet
      open={!!openFor}
      crew={openFor}
      actorId={user?.crew_id || user?.id || ""}
      onClose={() => setOpenFor(null)}
    />
  );
}

export default AppRouter;
